import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, ComposedChart,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import Navbar from '../components/Navbar';
import ScansBrowser from '../components/ScansBrowser';
import { getAnalyticsApiBaseUrl, isAnalyticsUser } from '../config/api';
import { fetchWithAuth } from '../utils/auth';

type ActiveTab = 'overview' | 'users' | 'videos' | 'engagement' | 'topUsers' | 'geography' | 'realtime' | 'aiLens' | 'devices' | 'scans' | 'subscriptions' | 'revenue' | 'userJourney' | 'userSegments' | 'leaderboard';

const COLORS = ['#FF9C15', '#F42026', '#ff6b35', '#fbbf24', '#fb923c', '#f97316', '#ef4444', '#dc2626', '#c084fc', '#38bdf8', '#34d399', '#a78bfa'];

const formatNumber = (n: number): string => {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(2) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
  return n.toLocaleString();
};

const formatDate = (dateStr: string): string => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatMonth = (year: number, month: number): string => {
  const d = new Date(year, month - 1);
  return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
};

const Analytics: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<ActiveTab>('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<Record<string, any>>({});
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const fetchData = useCallback(async (endpoint: string) => {
    const base = getAnalyticsApiBaseUrl();
    const res = await fetchWithAuth(`${base}/analytics/${endpoint}`);
    if (!res.ok) throw new Error(`Failed to fetch ${endpoint}`);
    return res.json();
  }, []);

  const loadAllData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const endpoints = [
        'overview', 'signups-daily', 'signups-hourly', 'signups-monthly',
        'signups-last30', 'signups-hourly-5d',
        'platforms', 'countries', 'cumulative-growth',
        'videos-overview', 'videos-daily', 'videos-monthly', 'videos-hourly-5d',
        'video-status-breakdown', 'creators-daily', 'creators-monthly',
        'reg-types', 'reg-types-daily',
        'engagement-daily', 'engagement-hourly',
        'top-users', 'recent-signups',
        'lens-overview', 'lens-mode-breakdown', 'lens-scans-daily',
        'lens-scans-monthly', 'lens-top-scanned', 'lens-chat-usage',
        'lens-diary-usage', 'lens-shares', 'lens-shared-opens',
        'lens-onboarding', 'lens-paywall', 'lens-daily-limits', 'lens-medication',
        'lens-my-health', 'lens-palm-kundli',
        'device-analytics',
        'subscription-funnel',
        'revenue-dashboard',
        'user-journey',
        'user-segments',
        'leaderboard-dashboard',
      ];

      const results = await Promise.allSettled(endpoints.map(e => fetchData(e)));
      const newData: Record<string, any> = {};
      results.forEach((r, i) => {
        if (r.status === 'fulfilled') newData[endpoints[i]] = r.value;
      });

      setData(newData);
      setLastRefresh(new Date());
    } catch (err: any) {
      console.error('Analytics fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [fetchData]);

  useEffect(() => {
    if (!isAnalyticsUser()) { navigate('/dashboard'); return; }
    loadAllData();
  }, [navigate, loadAllData]);

  useEffect(() => {
    const interval = setInterval(loadAllData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [loadAllData]);

  const overview = data['overview'];
  const videosOverview = data['videos-overview'];

  const brandGradient = 'from-[#FF9C15] to-[#F42026]';

  const tabs: { key: ActiveTab; label: string }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'realtime', label: 'Realtime' },
    { key: 'users', label: 'Users' },
    { key: 'videos', label: 'Videos' },
    { key: 'engagement', label: 'Engagement' },
    { key: 'topUsers', label: 'Top Users' },
    { key: 'geography', label: 'Geography' },
    { key: 'aiLens', label: 'AI Lens' },
    { key: 'devices', label: 'Devices' },
    { key: 'scans', label: 'Scans' },
    { key: 'subscriptions', label: 'Sub Funnel' },
    { key: 'revenue', label: 'Revenue' },
    { key: 'userJourney', label: 'User Journey' },
    { key: 'userSegments', label: 'Deep Segments' },
    { key: 'leaderboard', label: 'Leaderboard' },
  ];

  const StatCard = ({ title, value, subtitle, trend }: {
    title: string; value: string | number; subtitle?: string; trend?: string;
  }) => (
    <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 hover:bg-white/10 hover:border-[#FF9C15]/30 transition-all duration-300">
      <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">{title}</p>
      <p className="text-2xl font-bold text-white mt-1">{typeof value === 'number' ? formatNumber(value) : value}</p>
      <div className="flex items-center gap-2 mt-1">
        {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
        {trend && (
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
            parseFloat(trend) >= 0 ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'
          }`}>
            {parseFloat(trend) >= 0 ? '+' : ''}{trend}%
          </span>
        )}
      </div>
    </div>
  );

  const ChartCard = ({ title, children, className = '' }: {
    title: string; children: React.ReactNode; className?: string;
  }) => (
    <div className={`bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 ${className}`}>
      <h3 className="text-sm font-medium text-gray-400 mb-4">{title}</h3>
      {children}
    </div>
  );

  const tooltipStyle = { background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', color: '#fff' };

  // ─── Overview Tab ─────────────────────────────────────
  const renderOverview = () => {
    const engagement = overview?.engagement || {};
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard title="Total Active Users" value={overview?.totalUsers || 0} subtitle="Active accounts only" trend={overview?.growthRate} />
          <StatCard title="Today's Signups" value={overview?.todaySignups || 0} subtitle={`vs ${overview?.yesterdaySignups || 0} yesterday`} />
          <StatCard title="This Week" value={overview?.weekSignups || 0} subtitle="Last 7 days" />
          <StatCard title="This Month" value={overview?.monthSignups || 0} subtitle="Last 30 days" />
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard title="Total Active Videos" value={videosOverview?.totalVideos || 0} subtitle={`${videosOverview?.todayVideos || 0} today`} />
          <StatCard title="Verified Users" value={overview?.verifiedUsers || 0} subtitle={`${overview ? ((overview.verifiedUsers / overview.totalUsers) * 100).toFixed(1) : 0}% of total`} />
          <StatCard title="Unique Countries" value={overview?.uniqueCountries || 0} subtitle="Active user locations" />
          <StatCard title="Total DMs" value={overview?.totalDMs || 0} subtitle="Direct messages" />
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard title="Watches (30d)" value={engagement.watches || 0} />
          <StatCard title="Likes (30d)" value={engagement.likes || 0} />
          <StatCard title="Comments (30d)" value={engagement.comments || 0} />
          <StatCard title="Shares (30d)" value={engagement.shares || 0} />
          <StatCard title="Saves (30d)" value={engagement.saves || 0} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard title="Daily Signups (Last 90 Days)">
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={(data['signups-daily'] || []).map((d: any) => ({ ...d, date: formatDate(d.date) }))}>
                <defs>
                  <linearGradient id="signupGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#FF9C15" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#F42026" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="date" stroke="#64748b" fontSize={11} tickLine={false} interval="preserveStartEnd" />
                <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Area type="monotone" dataKey="count" stroke="#FF9C15" fill="url(#signupGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Cumulative User Growth">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={(data['cumulative-growth'] || []).map((d: any) => ({ ...d, label: formatMonth(d.year, d.month) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="label" stroke="#64748b" fontSize={11} tickLine={false} />
                <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => formatNumber(v)} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v: any) => [formatNumber(v), 'Total Users']} />
                <Line type="monotone" dataKey="totalUsers" stroke="#F42026" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <ChartCard title="Platform Distribution">
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={data['platforms'] || []} dataKey="count" nameKey="platform" cx="50%" cy="50%" outerRadius={80} label={({ name, percent }: any) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {(data['platforms'] || []).map((_: any, i: number) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Registration Source" className="lg:col-span-2">
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={data['reg-types'] || []} dataKey="count" nameKey="type" cx="50%" cy="50%" outerRadius={80} innerRadius={40} label={({ name, percent }: any) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {(data['reg-types'] || []).map((_: any, i: number) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        <ChartCard title="Monthly Signups (from Jul 2025)">
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={(data['signups-monthly'] || []).map((d: any) => ({ ...d, label: formatMonth(d.year, d.month) }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="label" stroke="#64748b" fontSize={11} tickLine={false} />
              <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="count" fill="#FF9C15" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    );
  };

  // ─── Realtime Tab ─────────────────────────────────────
  const renderRealtime = () => {
    const recentSignups = data['recent-signups'] || [];
    const hourlySignups = (data['signups-hourly'] || []).map((d: any) => ({ ...d, hour: `${d.hour}:00` }));

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          <StatCard title="Today's Signups" value={overview?.todaySignups || 0} subtitle="Users registered today" />
          <StatCard title="Today's Videos" value={videosOverview?.todayVideos || 0} subtitle="Videos uploaded today" />
          <StatCard title="Yesterday" value={overview?.yesterdaySignups || 0} subtitle="Signups yesterday" />
        </div>

        <ChartCard title="Signups by Hour (Today)">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={hourlySignups}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="hour" stroke="#64748b" fontSize={11} tickLine={false} />
              <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="count" fill="#FF9C15" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Recent Signups">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-400 border-b border-white/10">
                  <th className="pb-3 font-medium">User</th>
                  <th className="pb-3 font-medium">Country</th>
                  <th className="pb-3 font-medium">Platform</th>
                  <th className="pb-3 font-medium">Source</th>
                  <th className="pb-3 font-medium">Verified</th>
                  <th className="pb-3 font-medium">Joined</th>
                </tr>
              </thead>
              <tbody>
                {recentSignups.map((user: any, i: number) => (
                  <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                    <td className="py-3">
                      <div className="flex items-center gap-3">
                        {user.ProfileImageURL ? (
                          <img src={user.ProfileImageURL} alt="" className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FF9C15] to-[#F42026] flex items-center justify-center text-white text-xs font-bold">
                            {(user.UserName || user.Name || '?')[0].toUpperCase()}
                          </div>
                        )}
                        <div>
                          <p className="text-white font-medium">{user.Name || user.UserName || 'Anonymous'}</p>
                          <p className="text-gray-500 text-xs">@{user.UserName || 'n/a'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 text-gray-300">{user.Location || '-'}</td>
                    <td className="py-3 text-gray-300">{user.RegistrationDevice || '-'}</td>
                    <td className="py-3 text-gray-300">{user.RegistrationType || '-'}</td>
                    <td className="py-3">
                      {user.IsVerified ? (
                        <span className="text-emerald-400 text-xs bg-emerald-500/10 px-2 py-0.5 rounded-full">Yes</span>
                      ) : (
                        <span className="text-gray-500 text-xs">No</span>
                      )}
                    </td>
                    <td className="py-3 text-gray-400 text-xs">{new Date(user.createdAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ChartCard>
      </div>
    );
  };

  // ─── Users Tab ────────────────────────────────────────
  const renderUsers = () => {
    const dailySignups = (data['signups-daily'] || []).map((d: any) => ({ ...d, date: formatDate(d.date) }));
    const monthlySignups = (data['signups-monthly'] || []).map((d: any) => ({ ...d, label: formatMonth(d.year, d.month) }));
    const signupsLast30 = (data['signups-last30'] || []).map((d: any) => ({ ...d, date: formatDate(d.date) }));
    const hourly5d = data['signups-hourly-5d'] || [];
    const regTypesDaily = data['reg-types-daily'] || [];

    // Transform hourly 5d into chart format: group by date+hour
    const hourly5dFormatted = hourly5d.map((d: any) => ({
      ...d,
      label: `${d.date.slice(5)} ${d.hour}:00`,
    }));

    // Transform reg-types-daily into stacked bar chart format
    const regTypesDailyFormatted = regTypesDaily.map((d: any) => ({
      date: formatDate(d.date),
      ...d.data,
    }));
    const regTypeKeys = regTypesDaily.length > 0
      ? Array.from(new Set(regTypesDaily.flatMap((d: any) => Object.keys(d.data || {})))) as string[]
      : [];

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard title="Total Active Users" value={overview?.totalUsers || 0} />
          <StatCard title="Verified" value={overview?.verifiedUsers || 0} subtitle={`${overview ? ((overview.verifiedUsers / overview.totalUsers) * 100).toFixed(1) : 0}%`} />
          <StatCard title="Today" value={overview?.todaySignups || 0} />
          <StatCard title="Growth Rate" value={`${overview?.growthRate || 0}%`} subtitle="vs yesterday" />
        </div>

        <ChartCard title="Last 30 Days Signups">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={signupsLast30}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="date" stroke="#64748b" fontSize={11} tickLine={false} interval="preserveStartEnd" />
              <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="count" fill="#FF9C15" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Daily Signups (Last 90 Days)">
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={dailySignups}>
              <defs>
                <linearGradient id="userGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#FF9C15" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#F42026" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="date" stroke="#64748b" fontSize={11} tickLine={false} interval="preserveStartEnd" />
              <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Area type="monotone" dataKey="count" stroke="#FF9C15" fill="url(#userGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Signups Hourly (Last 5 Days)">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={hourly5dFormatted}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="label" stroke="#64748b" fontSize={9} tickLine={false} interval="preserveStartEnd" />
              <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="count" fill="#fb923c" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard title="Monthly Signups (from Jul 2025)">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={monthlySignups}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="label" stroke="#64748b" fontSize={11} tickLine={false} />
                <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="count" fill="#FF9C15" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Cumulative Growth">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={(data['cumulative-growth'] || []).map((d: any) => ({ ...d, label: formatMonth(d.year, d.month) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="label" stroke="#64748b" fontSize={11} tickLine={false} />
                <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => formatNumber(v)} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v: any) => [formatNumber(v), 'Total']} />
                <Line type="monotone" dataKey="totalUsers" stroke="#F42026" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        <ChartCard title="Registration by Source (Last 30 Days)">
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={regTypesDailyFormatted}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="date" stroke="#64748b" fontSize={10} tickLine={false} interval="preserveStartEnd" />
              <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend />
              {regTypeKeys.map((key, i) => (
                <Bar key={key} dataKey={key} stackId="regType" fill={COLORS[i % COLORS.length]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard title="Platform Distribution">
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={data['platforms'] || []} dataKey="count" nameKey="platform" cx="50%" cy="50%" outerRadius={100} innerRadius={60}>
                  {(data['platforms'] || []).map((_: any, i: number) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Registration Source (Total)">
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={data['reg-types'] || []} dataKey="count" nameKey="type" cx="50%" cy="50%" outerRadius={100} innerRadius={60}>
                  {(data['reg-types'] || []).map((_: any, i: number) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      </div>
    );
  };

  // ─── Videos Tab ───────────────────────────────────────
  const renderVideos = () => {
    const dailyVideos = (data['videos-daily'] || []).map((d: any) => ({ ...d, date: formatDate(d.date) }));
    const monthlyVideos = (data['videos-monthly'] || []).map((d: any) => ({ ...d, label: formatMonth(d.year, d.month) }));
    const hourly5d = (data['videos-hourly-5d'] || []).map((d: any) => ({ ...d, label: `${d.date.slice(5)} ${d.hour}:00` }));
    const statusBreakdown = data['video-status-breakdown'] || [];
    const creatorsDaily = (data['creators-daily'] || []).map((d: any) => ({ ...d, date: formatDate(d.date) }));
    const creatorsMonthly = data['creators-monthly'] || [];

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard title="Total Active Videos" value={videosOverview?.totalVideos || 0} />
          <StatCard title="Today" value={videosOverview?.todayVideos || 0} />
          <StatCard title="This Week" value={videosOverview?.weekVideos || 0} />
          <StatCard title="This Month" value={videosOverview?.monthVideos || 0} />
        </div>

        <ChartCard title="Video Status Breakdown">
          <div className="flex flex-col lg:flex-row items-center gap-6">
            <ResponsiveContainer width="50%" height={250}>
              <PieChart>
                <Pie data={statusBreakdown} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={80} innerRadius={40} label={({ name, percent }: any) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {statusBreakdown.map((_: any, i: number) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-2">
              {statusBreakdown.map((s: any, i: number) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                  <span className="text-sm text-gray-300">{s.status}</span>
                  <span className="text-sm text-white font-medium">{formatNumber(s.count)}</span>
                </div>
              ))}
            </div>
          </div>
        </ChartCard>

        <ChartCard title="Daily Video Uploads (Last 90 Days)">
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={dailyVideos}>
              <defs>
                <linearGradient id="videoGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#F42026" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#F42026" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="date" stroke="#64748b" fontSize={11} tickLine={false} interval="preserveStartEnd" />
              <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Area type="monotone" dataKey="count" stroke="#F42026" fill="url(#videoGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Video Uploads Hourly (Last 5 Days)">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={hourly5d}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="label" stroke="#64748b" fontSize={9} tickLine={false} interval="preserveStartEnd" />
              <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="count" fill="#F42026" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard title="Monthly Videos (from Jun 2025)">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={monthlyVideos}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="label" stroke="#64748b" fontSize={11} tickLine={false} />
                <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="count" fill="#F42026" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Creators Monthly (from Jun 2025)">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={creatorsMonthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="month" stroke="#64748b" fontSize={11} tickLine={false} />
                <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="creatorCount" fill="#FF9C15" name="Creators" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        <ChartCard title="Daily Creators & Videos (Last 30 Days)">
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={creatorsDaily}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="date" stroke="#64748b" fontSize={11} tickLine={false} interval="preserveStartEnd" />
              <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend />
              <Line type="monotone" dataKey="creatorCount" name="Unique Creators" stroke="#FF9C15" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="videoCount" name="Videos" stroke="#F42026" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    );
  };

  // ─── Engagement Tab ───────────────────────────────────
  const renderEngagement = () => {
    const engDaily = data['engagement-daily'] || {};
    const engHourly = data['engagement-hourly'] || {};
    const engagement = overview?.engagement || {};

    // Merge daily data into a single chart-friendly format
    const allDates = Array.from(new Set([
      ...(engDaily.watches || []).map((d: any) => d.date),
      ...(engDaily.likes || []).map((d: any) => d.date),
    ])).sort();

    const dailyMerged = allDates.map(date => {
      const findCount = (arr: any[]) => arr?.find((d: any) => d.date === date)?.count || 0;
      return {
        date: formatDate(date),
        watches: findCount(engDaily.watches),
        likes: findCount(engDaily.likes),
        comments: findCount(engDaily.comments),
        shares: findCount(engDaily.shares),
        saves: findCount(engDaily.saves),
      };
    });

    // Merge hourly data
    const allHourlyKeys = Array.from(new Set([
      ...(engHourly.watches || []).map((d: any) => `${d.date}|${d.hour}`),
      ...(engHourly.likes || []).map((d: any) => `${d.date}|${d.hour}`),
    ])).sort();

    const hourlyMerged = allHourlyKeys.map(key => {
      const [date, hour] = key.split('|');
      const findCount = (arr: any[]) => arr?.find((d: any) => d.date === date && String(d.hour) === hour)?.count || 0;
      return {
        label: `${date.slice(5)} ${hour}:00`,
        watches: findCount(engHourly.watches),
        likes: findCount(engHourly.likes),
        comments: findCount(engHourly.comments),
        shares: findCount(engHourly.shares),
        saves: findCount(engHourly.saves),
      };
    });

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard title="Watches (30d)" value={engagement.watches || 0} />
          <StatCard title="Likes (30d)" value={engagement.likes || 0} />
          <StatCard title="Comments (30d)" value={engagement.comments || 0} />
          <StatCard title="Shares (30d)" value={engagement.shares || 0} />
          <StatCard title="Saves (30d)" value={engagement.saves || 0} />
        </div>

        <ChartCard title="Daily Engagement (Last 30 Days)">
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={dailyMerged}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="date" stroke="#64748b" fontSize={10} tickLine={false} interval="preserveStartEnd" />
              <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => formatNumber(v)} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend />
              <Line type="monotone" dataKey="watches" name="Watches" stroke="#FF9C15" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="likes" name="Likes" stroke="#F42026" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="comments" name="Comments" stroke="#38bdf8" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="shares" name="Shares" stroke="#34d399" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="saves" name="Saves" stroke="#c084fc" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Hourly Engagement (Last 5 Days)">
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={hourlyMerged}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="label" stroke="#64748b" fontSize={9} tickLine={false} interval="preserveStartEnd" />
              <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => formatNumber(v)} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend />
              <Line type="monotone" dataKey="watches" name="Watches" stroke="#FF9C15" strokeWidth={1.5} dot={false} />
              <Line type="monotone" dataKey="likes" name="Likes" stroke="#F42026" strokeWidth={1.5} dot={false} />
              <Line type="monotone" dataKey="comments" name="Comments" stroke="#38bdf8" strokeWidth={1.5} dot={false} />
              <Line type="monotone" dataKey="shares" name="Shares" stroke="#34d399" strokeWidth={1.5} dot={false} />
              <Line type="monotone" dataKey="saves" name="Saves" stroke="#c084fc" strokeWidth={1.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    );
  };

  // ─── Top Users Tab ────────────────────────────────────
  const renderTopUsers = () => {
    const topData = data['top-users'] || {};
    const topFollowed = topData.topFollowed || [];
    const topUploaders = topData.topVideoUploaders || [];
    const topWatched = topData.topWatched || [];
    const topLiked = topData.topLiked || [];
    const topCommented = topData.topCommented || [];
    const topShared = topData.topShared || [];
    const topSaved = topData.topSaved || [];

    const UserRow = ({ user, metric, metricLabel }: { user: any; metric: number; metricLabel: string }) => (
      <div className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
        <div className="flex items-center gap-3">
          {user.ProfileImageURL ? (
            <img src={user.ProfileImageURL} alt="" className="w-8 h-8 rounded-full object-cover" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FF9C15] to-[#F42026] flex items-center justify-center text-white text-xs font-bold">
              {(user.UserName || user.Name || user.userName || user.name || '?')[0]?.toUpperCase() || '?'}
            </div>
          )}
          <div>
            <p className="text-white text-sm font-medium">{user.Name || user.name || user.UserName || user.userName || 'Anonymous'}</p>
            <p className="text-gray-500 text-xs">@{user.UserName || user.userName || 'n/a'}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-white font-semibold">{formatNumber(metric)}</p>
          <p className="text-gray-500 text-xs">{metricLabel}</p>
        </div>
      </div>
    );

    const VideoRow = ({ item, metricLabel }: { item: any; metricLabel: string }) => (
      <div className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
        <p className="text-gray-300 text-sm truncate max-w-[200px]">{item.videoId || 'N/A'}</p>
        <div className="text-right">
          <p className="text-white font-semibold">{formatNumber(item.count)}</p>
          <p className="text-gray-500 text-xs">{metricLabel}</p>
        </div>
      </div>
    );

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard title="Most Followed Users (Top 20)">
            <div className="max-h-[500px] overflow-y-auto space-y-0">
              {topFollowed.map((u: any, i: number) => (
                <UserRow key={i} user={u} metric={u.FollowersCount || 0} metricLabel="followers" />
              ))}
            </div>
          </ChartCard>

          <ChartCard title="Top Video Uploaders (Top 20)">
            <div className="max-h-[500px] overflow-y-auto space-y-0">
              {topUploaders.map((u: any, i: number) => (
                <UserRow key={i} user={u} metric={u.videoCount || 0} metricLabel="videos" />
              ))}
            </div>
          </ChartCard>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <ChartCard title="Top Watched Videos">
            <div className="max-h-[400px] overflow-y-auto">
              {topWatched.map((v: any, i: number) => <VideoRow key={i} item={v} metricLabel="watches" />)}
            </div>
          </ChartCard>

          <ChartCard title="Top Liked Videos">
            <div className="max-h-[400px] overflow-y-auto">
              {topLiked.map((v: any, i: number) => <VideoRow key={i} item={v} metricLabel="likes" />)}
            </div>
          </ChartCard>

          <ChartCard title="Top Commented Videos">
            <div className="max-h-[400px] overflow-y-auto">
              {topCommented.map((v: any, i: number) => <VideoRow key={i} item={v} metricLabel="comments" />)}
            </div>
          </ChartCard>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard title="Top Shared Videos">
            <div className="max-h-[400px] overflow-y-auto">
              {topShared.map((v: any, i: number) => <VideoRow key={i} item={v} metricLabel="shares" />)}
            </div>
          </ChartCard>

          <ChartCard title="Top Saved Videos">
            <div className="max-h-[400px] overflow-y-auto">
              {topSaved.map((v: any, i: number) => <VideoRow key={i} item={v} metricLabel="saves" />)}
            </div>
          </ChartCard>
        </div>
      </div>
    );
  };

  // ─── Geography Tab ────────────────────────────────────
  const renderGeography = () => {
    const countries = data['countries'] || [];
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <StatCard title="Unique Countries" value={overview?.uniqueCountries || 0} subtitle="Active user locations" />
          <StatCard title="Top Country" value={countries[0]?.country || '-'} subtitle={countries[0] ? formatNumber(countries[0].count) + ' users' : ''} />
        </div>

        <ChartCard title="Top 20 Countries by Active Users">
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={countries.slice(0, 20)} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis type="number" stroke="#64748b" fontSize={11} tickLine={false} tickFormatter={(v) => formatNumber(v)} />
              <YAxis type="category" dataKey="country" stroke="#64748b" fontSize={11} tickLine={false} width={100} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="count" fill="#FF9C15" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="All Countries">
          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {countries.map((c: any, i: number) => {
              const maxCount = countries[0]?.count || 1;
              const percentage = (c.count / maxCount) * 100;
              return (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-xs text-gray-400 w-6 text-right">{i + 1}</span>
                  <span className="text-sm text-white w-32 truncate">{c.country}</span>
                  <div className="flex-1 bg-white/5 rounded-full h-6 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-[#FF9C15] to-[#F42026] rounded-full flex items-center justify-end pr-2"
                      style={{ width: `${Math.max(percentage, 5)}%` }}
                    >
                      <span className="text-xs text-white font-medium">{formatNumber(c.count)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </ChartCard>
      </div>
    );
  };

  // ─── AI Lens Tab ─────────────────────────────────────
  const renderAiLens = () => {
    const lens = data['lens-overview'] || {};
    const modeBreakdown = data['lens-mode-breakdown'] || [];
    const scanDaily = data['lens-scans-daily'] || [];
    const scanMonthly = data['lens-scans-monthly'] || [];
    const topScanned = data['lens-top-scanned'] || [];
    const chatUsage = data['lens-chat-usage'] || {};
    const diaryUsage = data['lens-diary-usage'] || {};
    const shares = data['lens-shares'] || {};
    const sharedOpens = data['lens-shared-opens'] || {};
    const onboarding = data['lens-onboarding'] || {};
    const paywall = data['lens-paywall'] || {};
    const dailyLimits = data['lens-daily-limits'] || {};
    const medication = data['lens-medication'] || {};
    const myHealth = data['lens-my-health'] || {};
    const palmKundli = data['lens-palm-kundli'] || {};

    const MODE_COLORS: Record<string, string> = {
      food: '#f97316', medicine: '#3b82f6', plant: '#22c55e', repair: '#d97706',
      hygiene: '#eab308', product: '#a855f7', animal: '#14b8a6', document: '#6366f1',
    };

    // Format daily scans for stacked bar chart
    const allModes = Array.from(new Set(modeBreakdown.map((m: any) => m.mode))) as string[];
    const dailyForChart = scanDaily.map((d: any) => ({
      date: formatDate(d.date),
      total: d.count,
      ...(d.byMode || {}),
    }));

    // Share destinations for pie chart
    const shareDests = Object.entries(shares.byDest || {}).map(([dest, count]) => ({
      name: dest.replace('_', ' '),
      value: count as number,
    }));

    // Paywall funnel
    const funnelData = [
      { step: 'Views', value: paywall.views || 0 },
      { step: 'Checkout Start', value: paywall.checkoutStarts || 0 },
      { step: 'Success', value: paywall.successes || 0 },
      { step: 'Failed', value: paywall.fails || 0 },
    ];

    return (
      <div className="space-y-6">
        {/* Row 1: Key metrics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard title="Total Scans" value={lens.totalScans || 0} subtitle={`${lens.todayScans || 0} today`} />
          <StatCard title="Unique Scan Users" value={lens.uniqueScanUsers || 0} />
          <StatCard title="Chat Messages" value={lens.totalChatMessages || 0} subtitle={`${lens.uniqueChatUsers || 0} users chatted`} />
          <StatCard title="AI Cost" value={`$${(lens.totalAiCost || 0).toFixed(2)}`} subtitle="Total estimated" />
        </div>

        {/* Row 2: Feature usage */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard title="Food Diary Entries" value={diaryUsage.totalEntries || 0} subtitle={`${diaryUsage.uniqueUsers || 0} users, ${diaryUsage.conversionRate || 0}% from scans`} />
          <StatCard title="Scans Shared" value={lens.totalShared || 0} />
          <StatCard title="Med Reminders Set" value={medication.remindersSet || 0} subtitle={`${medication.uniqueUsers || 0} unique users`} />
          <StatCard title="Daily Limit Hits" value={dailyLimits.totalHits || 0} subtitle={`${dailyLimits.uniqueUsers || 0} unique users`} />
        </div>

        {/* Row 3: Onboarding + Subscriptions */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard title="Onboarding Started" value={onboarding.started || lens.onboardingTotal || 0} />
          <StatCard title="Onboarding Completed" value={onboarding.completed || lens.onboardingComplete || 0}
            subtitle={`${(onboarding.started || lens.onboardingTotal) > 0
              ? ((((onboarding.completed || lens.onboardingComplete) / (onboarding.started || lens.onboardingTotal || 1)) * 100).toFixed(1))
              : 0}% completion`} />
          <StatCard title="Shared Link Opens" value={sharedOpens.total || 0} subtitle={`${sharedOpens.uniqueUsers || 0} unique, ${sharedOpens.saves || 0} saved`} />
          <StatCard title="Paywall Views" value={paywall.views || 0}
            subtitle={`${paywall.successes || 0} converted (${paywall.views > 0 ? ((paywall.successes / paywall.views) * 100).toFixed(1) : 0}%)`} />
        </div>

        {/* Tier breakdown */}
        {lens.tierBreakdown && lens.tierBreakdown.length > 0 && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {lens.tierBreakdown.map((t: any) => (
              <StatCard key={t.tier} title={`${t.tier.charAt(0).toUpperCase() + t.tier.slice(1)} Users`} value={t.count} />
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Mode breakdown pie */}
          <ChartCard title="Scans by Lens Mode">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={modeBreakdown} dataKey="count" nameKey="mode" cx="50%" cy="50%" outerRadius={100} label={({ mode, pct }: any) => `${mode} ${pct}%`}>
                  {modeBreakdown.map((m: any, i: number) => (
                    <Cell key={m.mode} fill={MODE_COLORS[m.mode] || COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Share destinations */}
          <ChartCard title="Share Destinations (30d)">
            {shareDests.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={shareDests} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={({ name, value }: any) => `${name}: ${value}`}>
                    {shareDests.map((_: any, i: number) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-gray-500">No share events tracked yet</div>
            )}
          </ChartCard>
        </div>

        {/* Daily scans chart */}
        <ChartCard title="Daily Scans (Last 30 Days)">
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={dailyForChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="date" tick={{ fill: '#9ca3af', fontSize: 11 }} interval={4} />
              <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend />
              {allModes.map((mode, i) => (
                <Bar key={mode} dataKey={mode} stackId="scans" fill={MODE_COLORS[mode] || COLORS[i % COLORS.length]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Monthly scans */}
        <ChartCard title="Monthly Scans">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={scanMonthly.map((m: any) => ({ month: formatMonth(m.year, m.month), count: m.count }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="month" tick={{ fill: '#9ca3af', fontSize: 11 }} />
              <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="count" fill="#FF9C15" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Chat usage daily */}
          <ChartCard title="Ask AI Chat Usage (30d)">
            {chatUsage.daily && chatUsage.daily.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={chatUsage.daily.map((d: any) => ({ date: formatDate(d.date), messages: d.messages, users: d.uniqueUsers }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="date" tick={{ fill: '#9ca3af', fontSize: 11 }} interval={4} />
                  <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Area type="monotone" dataKey="messages" stroke="#a855f7" fill="#a855f7" fillOpacity={0.2} name="Messages" />
                  <Area type="monotone" dataKey="users" stroke="#22c55e" fill="#22c55e" fillOpacity={0.2} name="Unique Users" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[250px] text-gray-500">Chat data loading...</div>
            )}
          </ChartCard>

          {/* Paywall funnel */}
          <ChartCard title="Paywall Funnel (30d)">
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={funnelData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis type="number" tick={{ fill: '#9ca3af', fontSize: 11 }} />
                <YAxis type="category" dataKey="step" tick={{ fill: '#9ca3af', fontSize: 11 }} width={110} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {funnelData.map((_, i) => (
                    <Cell key={i} fill={['#FF9C15', '#f97316', '#22c55e', '#ef4444'][i]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            {paywall.bySource && Object.keys(paywall.bySource).length > 0 && (
              <div className="mt-3">
                <p className="text-xs text-gray-500 mb-1">Views by Source:</p>
                <div className="flex flex-wrap gap-3">
                  {Object.entries(paywall.bySource).map(([source, count]) => (
                    <span key={source} className="text-xs text-gray-400">
                      {source.replace(/_/g, ' ')}: <span className="text-white font-medium">{count as number}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
            {paywall.byPlan && Object.keys(paywall.byPlan).length > 0 && (
              <div className="mt-3">
                <p className="text-xs text-gray-500 mb-1">Checkouts by Plan:</p>
                <div className="flex gap-4">
                  {Object.entries(paywall.byPlan).map(([plan, count]) => (
                    <span key={plan} className="text-xs text-gray-400">
                      {plan}: <span className="text-white font-medium">{count as number}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </ChartCard>
        </div>

        {/* Onboarding step dropoff */}
        {onboarding.stepDropoff && onboarding.stepDropoff.length > 0 && (
          <ChartCard title="Onboarding Step Dropoff">
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={onboarding.stepDropoff.map((s: any) => ({ step: `${s.type} Step ${s.step}`, users: s.count }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="step" tick={{ fill: '#9ca3af', fontSize: 11 }} />
                <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="users" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        )}

        {/* Daily limit hits chart */}
        {dailyLimits.daily && dailyLimits.daily.length > 0 && (
          <ChartCard title="Daily Scan Limit Hits (30d)">
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={dailyLimits.daily.map((d: any) => ({ date: formatDate(d.date), count: d.count }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="date" tick={{ fill: '#9ca3af', fontSize: 11 }} interval={4} />
                <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} />
                <Tooltip contentStyle={tooltipStyle} />
                <Area type="monotone" dataKey="count" stroke="#ef4444" fill="#ef4444" fillOpacity={0.2} />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        )}

        {/* Top scanned items */}
        {topScanned.length > 0 && (
          <ChartCard title="Top Scanned Items">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[400px] overflow-y-auto">
              {topScanned.map((item: any, i: number) => (
                <div key={i} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-bold text-gray-500 w-6 text-right">{i + 1}</span>
                    <div>
                      <p className="text-sm font-medium text-white">{item.itemName}</p>
                      <p className="text-xs text-gray-400">{item.mode}</p>
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-white">{formatNumber(item.count)}</span>
                </div>
              ))}
            </div>
          </ChartCard>
        )}

        {/* ── My Health ── */}
        <h2 className="text-lg font-bold text-white mt-8 mb-4 flex items-center gap-2">
          <span className="text-xl">🏥</span> My Health (30d)
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard title="Setup Started" value={myHealth.setupStarts || 0} />
          <StatCard title="Setup Completed" value={myHealth.setupCompletes || 0} subtitle={myHealth.setupStarts > 0 ? `${((myHealth.setupCompletes / myHealth.setupStarts) * 100).toFixed(1)}% completion` : ''} />
          <StatCard title="Daily Opens" value={myHealth.dailyOpens || 0} subtitle={`${myHealth.dailyOpenUsers || 0} unique users`} />
          <StatCard title="Feature Interactions" value={myHealth.featureUses || 0} />
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
          <StatCard title="Medication Taken" value={myHealth.medicationTaken || 0} subtitle={`${myHealth.medicationTakenUsers || 0} unique users`} />
          <StatCard title="Reminders Set" value={myHealth.remindersSet || 0} subtitle={`${myHealth.reminderUsers || 0} unique users`} />
        </div>

        {/* Health feature breakdown */}
        {myHealth.byFeature && Object.keys(myHealth.byFeature).length > 0 && (
          <ChartCard title="Health Feature Usage (30d)" className="mt-4">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={Object.entries(myHealth.byFeature).map(([f, c]) => ({ feature: f.replace('_', ' '), count: c as number }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="feature" tick={{ fill: '#999', fontSize: 12 }} />
                <YAxis tick={{ fill: '#999', fontSize: 12 }} />
                <Tooltip contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333', borderRadius: 8, color: '#fff' }} />
                <Bar dataKey="count" fill="#7B61FF" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        )}

        {/* Health medication taken daily */}
        {myHealth.medicationTakenDaily && myHealth.medicationTakenDaily.length > 0 && (
          <ChartCard title="Medication Doses Taken (Daily, 30d)" className="mt-4">
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={myHealth.medicationTakenDaily.map((d: any) => ({ date: formatDate(d.date), count: d.count }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="date" tick={{ fill: '#999', fontSize: 11 }} />
                <YAxis tick={{ fill: '#999', fontSize: 12 }} />
                <Tooltip contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333', borderRadius: 8, color: '#fff' }} />
                <Area type="monotone" dataKey="count" stroke="#22c55e" fill="#22c55e" fillOpacity={0.2} />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        )}

        {/* ── Palm Kundli ── */}
        <h2 className="text-lg font-bold text-white mt-8 mb-4 flex items-center gap-2">
          <span className="text-xl">🔮</span> Palm Kundli (30d)
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard title="Profiles Created" value={palmKundli.profilesCreated || 0} subtitle={`${palmKundli.profileCreators || 0} users, avg ${palmKundli.avgProfilesPerUser || 0}/user`} />
          <StatCard title="Daily Opens" value={palmKundli.dailyOpens || 0} subtitle={`${palmKundli.dailyOpenUsers || 0} unique users`} />
          <StatCard title="Palm Scans" value={palmKundli.palmScanCompletes || 0} subtitle={`${palmKundli.palmScanUsers || 0} users, ${palmKundli.palmScanStarts || 0} started`} />
          <StatCard title="Chat Messages" value={palmKundli.chatMessages || 0} subtitle={`${palmKundli.chatUsers || 0} unique users`} />
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
          <StatCard title="Compatibility Checks" value={palmKundli.compatibilityChecks || 0} subtitle={`${palmKundli.compatibilityUsers || 0} unique users`} />
          <StatCard title="Feature Interactions" value={palmKundli.featureUses || 0} />
        </div>

        {/* Kundli feature & language breakdown side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
          {palmKundli.byFeature && Object.keys(palmKundli.byFeature).length > 0 && (
            <ChartCard title="Kundli Tab Usage">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={Object.entries(palmKundli.byFeature).map(([f, c]) => ({ tab: f, count: c as number }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="tab" tick={{ fill: '#999', fontSize: 12 }} />
                  <YAxis tick={{ fill: '#999', fontSize: 12 }} />
                  <Tooltip contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333', borderRadius: 8, color: '#fff' }} />
                  <Bar dataKey="count" fill="#8B5CF6" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          )}
          {palmKundli.byLanguage && Object.keys(palmKundli.byLanguage).length > 0 && (
            <ChartCard title="Language Distribution">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={Object.entries(palmKundli.byLanguage).map(([lang, count]) => ({ name: lang, value: count as number }))}
                    cx="50%" cy="50%" innerRadius={50} outerRadius={80}
                    paddingAngle={3} dataKey="value" label={({ name, percent }: any) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                  >
                    {Object.keys(palmKundli.byLanguage).map((_: string, i: number) => (
                      <Cell key={i} fill={['#8B5CF6', '#6366F1', '#3B82F6', '#14B8A6', '#F59E0B', '#EF4444', '#EC4899', '#10B981', '#F97316', '#6B7280'][i % 10]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333', borderRadius: 8, color: '#fff' }} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          )}
        </div>
      </div>
    );
  };

  // ─── Devices Tab ──────────────────────────────────────
  const renderDevices = () => {
    const da = data['device-analytics'] || {};
    const appVersions = da.appVersions || [];
    const androidOsVersions = da.androidOsVersions || [];
    const iosOsVersions = da.iosOsVersions || [];
    const topDeviceModels = da.topDeviceModels || [];
    const androidAppVersions = da.androidAppVersions || [];
    const iosAppVersions = da.iosAppVersions || [];
    const dailyPlatform = da.dailyPlatform || [];

    const totalAndroid = dailyPlatform.reduce((s: number, d: any) => s + (d.Android || 0), 0);
    const totalIos = dailyPlatform.reduce((s: number, d: any) => s + (d.iOS || 0), 0);

    const RankTable = ({ rows, labelKey, title }: { rows: any[]; labelKey: string; title: string }) => (
      <ChartCard title={title}>
        <div className="max-h-80 overflow-y-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-gray-400 border-b border-white/10">
              <th className="pb-2 font-medium w-10">#</th>
              <th className="pb-2 font-medium">{labelKey === 'version' ? 'Version' : labelKey === 'model' ? 'Device' : 'OS Version'}</th>
              {labelKey === 'model' && <th className="pb-2 font-medium">Platform</th>}
              <th className="pb-2 font-medium text-right">Users</th>
            </tr></thead>
            <tbody>
              {rows.map((r: any, i: number) => (
                <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                  <td className="py-2 text-gray-500">{i + 1}</td>
                  <td className="py-2 text-white font-mono text-xs">{r[labelKey]}</td>
                  {labelKey === 'model' && <td className="py-2 text-gray-400 text-xs">{r.platform}</td>}
                  <td className="py-2 text-right text-white font-semibold">{formatNumber(r.count)}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={4} className="py-4 text-center text-gray-500">No data yet</td></tr>}
            </tbody>
          </table>
        </div>
      </ChartCard>
    );

    return (
      <div className="space-y-6">
        {/* Platform trend */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard title="Android Signups (30d)" value={totalAndroid} />
          <StatCard title="iOS Signups (30d)" value={totalIos} />
          <StatCard title="Android %" value={totalAndroid + totalIos > 0 ? `${((totalAndroid / (totalAndroid + totalIos)) * 100).toFixed(1)}%` : '—'} />
          <StatCard title="iOS %" value={totalAndroid + totalIos > 0 ? `${((totalIos / (totalAndroid + totalIos)) * 100).toFixed(1)}%` : '—'} />
        </div>

        {dailyPlatform.length > 0 && (
          <ChartCard title="Daily Signups by Platform (Last 30 Days)">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={dailyPlatform}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="date" stroke="#9ca3af" tick={{ fill: '#9ca3af', fontSize: 11 }} tickFormatter={formatDate} />
                <YAxis stroke="#9ca3af" tick={{ fill: '#9ca3af', fontSize: 11 }} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend />
                <Bar dataKey="Android" fill="#34d399" stackId="a" />
                <Bar dataKey="iOS" fill="#38bdf8" stackId="a" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        )}

        {/* App Versions - Android vs iOS side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <RankTable rows={androidAppVersions} labelKey="version" title="App Versions (Android)" />
          <RankTable rows={iosAppVersions} labelKey="version" title="App Versions (iOS)" />
        </div>

        {/* OS Versions - Android vs iOS side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <RankTable rows={androidOsVersions} labelKey="osVersion" title="OS Versions (Android)" />
          <RankTable rows={iosOsVersions} labelKey="osVersion" title="OS Versions (iOS)" />
        </div>

        {/* Overall app versions chart + top devices */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {appVersions.length > 0 && (
            <ChartCard title="Top App Versions (All Platforms)">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={appVersions.slice(0, 15)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis type="number" stroke="#9ca3af" tick={{ fill: '#9ca3af', fontSize: 11 }} />
                  <YAxis dataKey="version" type="category" width={120} stroke="#9ca3af" tick={{ fill: '#9ca3af', fontSize: 10 }} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Bar dataKey="count" fill="#FF9C15" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          )}
          <RankTable rows={topDeviceModels} labelKey="model" title="Top Device Models" />
        </div>
      </div>
    );
  };

  // ─── Scans Browser Tab ────────────────────────────────
  const renderScans = () => <ScansBrowser />;

  // ─── Subscription Funnel Tab ──────────────────────────
  const renderSubscriptions = () => {
    const funnel = data['subscription-funnel'] || {};
    const tf = funnel.trialFunnel || {};
    const sl = funnel.subLifecycle || {};
    const ip = sl.initialPurchases || {};
    const renewals = sl.renewals || {};
    const cancellations = sl.cancellations || {};
    const expirations = sl.expirations || {};

    // Funnel bar chart data
    const funnelSteps = [
      { step: 'Signups (24h)', count: funnel.last24hSignups || 0 },
      { step: 'Shown Paywall', count: funnel.paywallGatedUsers || 0 },
      { step: 'Paywall Views', count: tf.viewUsers || 0 },
      { step: 'Checkout Start', count: tf.checkoutStartUsers || 0 },
      { step: 'Subscribed', count: tf.successUsers || 0 },
      { step: 'Timer Expired', count: tf.expireUsers || 0 },
    ];

    // Plan breakdown for pie chart
    const planData = Object.entries(tf.successByPlan || {}).map(([plan, count]) => ({
      name: plan === 'yearly_trial' ? '3-Day Free Trial (Yearly)' : plan === 'weekly' ? 'Weekly' : plan === 'yearly' ? 'Yearly Direct' : plan,
      value: count as number,
    }));

    // Cancellation reasons pie
    const cancelReasonData = Object.entries(cancellations.byReason || {}).map(([reason, count]) => ({
      name: reason, value: count as number,
    }));

    // Daily subscription lifecycle chart
    const dailySub = sl.daily || [];

    // Daily trial funnel chart
    const dailyFunnel = tf.dailyFunnel || [];

    return (
      <div className="space-y-6">
        {/* ── Top Stats ── */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard title="Paywall Gated Users" value={funnel.paywallGatedUsers || 0} subtitle="Total users shown trial" />
          <StatCard title="Subscribed" value={funnel.paywallGatedSubscribed || 0} subtitle={`${funnel.paywallGatedConversion || 0}% conversion`} />
          <StatCard title="Trial Paywall Views" value={tf.viewUsers || 0} subtitle={`${tf.conversionRate || 0}% convert`} />
          <StatCard title="Timer Expired" value={tf.expireUsers || 0} subtitle={`${tf.abandonRate || 0}% abandon rate`} />
          <StatCard title="Signups (24h)" value={funnel.last24hSignups || 0} subtitle="New registrations" />
        </div>

        {/* ── Onboarding → Subscription Funnel ── */}
        <ChartCard title="Onboarding → Subscription Funnel">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={funnelSteps} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis type="number" stroke="#64748b" fontSize={11} tickLine={false} />
              <YAxis type="category" dataKey="step" stroke="#64748b" fontSize={11} tickLine={false} width={120} />
              <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }} />
              <Bar dataKey="count" fill="#FF9C15" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* ── Plan Distribution ── */}
          <ChartCard title="Subscription Plan Chosen">
            {planData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie data={planData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, percent }: any) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}>
                    {planData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }} />
                </PieChart>
              </ResponsiveContainer>
            ) : <p className="text-gray-500 text-center py-8">No subscription data yet</p>}
          </ChartCard>

          {/* ── Checkout by Plan ── */}
          <ChartCard title="Trial Paywall Details">
            <div className="space-y-3 p-2">
              <div className="flex justify-between text-sm"><span className="text-gray-400">Checkout Starts</span><span className="text-white font-semibold">{tf.checkoutStarts || 0}</span></div>
              <div className="flex justify-between text-sm"><span className="text-gray-400">Successes</span><span className="text-green-400 font-semibold">{tf.successes || 0}</span></div>
              <div className="flex justify-between text-sm"><span className="text-gray-400">Failures</span><span className="text-red-400 font-semibold">{tf.fails || 0}</span></div>
              <div className="flex justify-between text-sm"><span className="text-gray-400">Timer Expires</span><span className="text-yellow-400 font-semibold">{tf.expires || 0}</span></div>
              <div className="flex justify-between text-sm"><span className="text-gray-400">Plan Selects</span><span className="text-white font-semibold">{JSON.stringify(tf.planSelects || {})}</span></div>
              <div className="flex justify-between text-sm"><span className="text-gray-400">Follow Actions</span><span className="text-white font-semibold">{tf.followUsers || 0} users</span></div>
              <hr className="border-white/10" />
              <div className="flex justify-between text-sm"><span className="text-gray-400">Checkout by Plan</span></div>
              {Object.entries(tf.checkoutByPlan || {}).map(([plan, count]) => (
                <div key={plan} className="flex justify-between text-sm pl-4"><span className="text-gray-500">{plan}</span><span className="text-white">{count as number}</span></div>
              ))}
            </div>
          </ChartCard>
        </div>

        {/* ── Daily Trial Funnel ── */}
        {dailyFunnel.length > 0 && (
          <ChartCard title="Daily Trial Paywall (Views / Subscribed / Expired)">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={dailyFunnel.map((d: any) => ({ ...d, date: formatDate(d.date) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="date" stroke="#64748b" fontSize={11} tickLine={false} />
                <YAxis stroke="#64748b" fontSize={11} tickLine={false} />
                <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }} />
                <Legend />
                <Bar dataKey="views" fill="#FF9C15" name="Views" />
                <Bar dataKey="successes" fill="#4ade80" name="Subscribed" />
                <Bar dataKey="expires" fill="#f87171" name="Expired" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        )}

        {/* ── Subscription Lifecycle (RevenueCat) ── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard title="Purchases (30d)" value={ip.total || 0} subtitle={`${ip.trialStarts || 0} trials, ${ip.weeklyStarts || 0} weekly, ${ip.yearlyDirect || 0} yearly`} />
          <StatCard title="Renewals (30d)" value={renewals.total || 0} subtitle="Active subscribers renewing" />
          <StatCard title="Cancellations (30d)" value={cancellations.total || 0} subtitle="Turned off auto-renew" />
          <StatCard title="Expirations (30d)" value={expirations.total || 0} subtitle="Subscription ended" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* ── Cancellation Reasons ── */}
          <ChartCard title="Cancellation Reasons (30d)">
            {cancelReasonData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie data={cancelReasonData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, percent }: any) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}>
                    {cancelReasonData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }} />
                </PieChart>
              </ResponsiveContainer>
            ) : <p className="text-gray-500 text-center py-8">No cancellation data yet</p>}
          </ChartCard>

          {/* ── Purchases by Plan (30d) ── */}
          <ChartCard title="Purchases by Plan (30d)">
            <div className="space-y-3 p-2">
              {Object.entries(ip.byPlan || {}).map(([plan, count]) => (
                <div key={plan} className="flex justify-between text-sm">
                  <span className="text-gray-400">{plan}</span>
                  <span className="text-white font-semibold">{count as number}</span>
                </div>
              ))}
              {Object.keys(ip.byPlan || {}).length === 0 && <p className="text-gray-500 text-center py-4">No purchase data yet</p>}
            </div>
          </ChartCard>
        </div>

        {/* ── Daily Subscription Lifecycle ── */}
        {dailySub.length > 0 && (
          <ChartCard title="Daily Subscription Events (30d)">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={dailySub.map((d: any) => ({ ...d, date: formatDate(d.date) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="date" stroke="#64748b" fontSize={11} tickLine={false} />
                <YAxis stroke="#64748b" fontSize={11} tickLine={false} />
                <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }} />
                <Legend />
                <Line type="monotone" dataKey="purchases" stroke="#FF9C15" strokeWidth={2} dot={false} name="Purchases" />
                <Line type="monotone" dataKey="renewals" stroke="#4ade80" strokeWidth={2} dot={false} name="Renewals" />
                <Line type="monotone" dataKey="cancellations" stroke="#f87171" strokeWidth={2} dot={false} name="Cancellations" />
                <Line type="monotone" dataKey="expirations" stroke="#fbbf24" strokeWidth={2} dot={false} name="Expirations" />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        )}

        {/* ── Renewal by Plan ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard title="Renewals by Plan (30d)">
            <div className="space-y-3 p-2">
              {Object.entries(renewals.byPlan || {}).map(([plan, count]) => (
                <div key={plan} className="flex justify-between text-sm">
                  <span className="text-gray-400">{plan}</span>
                  <span className="text-green-400 font-semibold">{count as number}</span>
                </div>
              ))}
              {Object.keys(renewals.byPlan || {}).length === 0 && <p className="text-gray-500 text-center py-4">No renewal data yet</p>}
            </div>
          </ChartCard>

          <ChartCard title="Cancellations by Plan (30d)">
            <div className="space-y-3 p-2">
              {Object.entries(cancellations.byPlan || {}).map(([plan, count]) => (
                <div key={plan} className="flex justify-between text-sm">
                  <span className="text-gray-400">{plan}</span>
                  <span className="text-red-400 font-semibold">{count as number}</span>
                </div>
              ))}
              {Object.keys(cancellations.byPlan || {}).length === 0 && <p className="text-gray-500 text-center py-4">No cancellation data yet</p>}
            </div>
          </ChartCard>
        </div>
      </div>
    );
  };

  // ─── Revenue Dashboard Tab ──────────────────────────────
  const renderRevenue = () => {
    const rd = data['revenue-dashboard'] || {};
    const dailyRev = rd.dailyRevenue || [];
    const todayProducts = rd.todayProducts || [];
    const todayCountries = rd.todayByCountry || [];
    const trialToday = rd.trialToday || [];
    const variantFunnel = rd.variantFunnel || [];
    const sourceFunnel = rd.sourceFunnel || [];
    const recentOrders = rd.recentOrders || [];
    const checkoutTiming = rd.checkoutTiming || [];
    const closeTimingData = rd.closeTiming || [];
    const quizStats = rd.quizStats || {};

    // Build variant funnel table
    const variants = ['trial', 'simple', 'coins'];
    const funnelEvents = ['trial_paywall_view', 'trial_paywall_plan_select', 'trial_paywall_checkout_start', 'trial_paywall_checkout_success', 'trial_paywall_checkout_fail', 'paywall_close_button_tap'];
    const eventLabels: Record<string, string> = {
      'trial_paywall_view': 'Views',
      'trial_paywall_plan_select': 'Plan Select',
      'trial_paywall_checkout_start': 'Checkout',
      'trial_paywall_checkout_success': 'Success',
      'trial_paywall_checkout_fail': 'Fail',
      'paywall_close_button_tap': 'Close',
    };
    const vfMap: Record<string, Record<string, number>> = {};
    for (const v of variantFunnel) {
      if (!vfMap[v.variant]) vfMap[v.variant] = {};
      vfMap[v.variant][v.event] = v.uniqueUsers;
    }

    // Build source funnel table
    const sfMap: Record<string, Record<string, number>> = {};
    for (const s of sourceFunnel) {
      if (!sfMap[s.source]) sfMap[s.source] = {};
      sfMap[s.source][s.event] = s.uniqueUsers;
    }
    const sortedSources = Object.entries(sfMap)
      .map(([source, events]) => ({ source, views: events['trial_paywall_view'] || 0, checkout: events['trial_paywall_checkout_start'] || 0, success: events['trial_paywall_checkout_success'] || 0, close: events['paywall_close_button_tap'] || 0 }))
      .filter(s => s.views > 0)
      .sort((a, b) => b.views - a.views);

    // Trial funnel map
    const trialMap: Record<string, any> = {};
    for (const t of trialToday) trialMap[t.type] = t;

    const tooltipStyle = { background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' };

    return (
      <div className="space-y-6">
        {/* ── Top Stats ── */}
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
          <StatCard title="Today Revenue" value={`$${(rd.todayRevenue || 0).toFixed(2)}`} subtitle={(() => { const today = (rd.dailyRevenue || []).slice(-1)[0] || {}; return `Subs $${(today.subs || 0).toFixed(0)} · Coins $${(today.coins || 0).toFixed(0)} · Tokens $${(today.diamonds || 0).toFixed(0)} · Streak $${(today.streak || 0).toFixed(0)}`; })()} />
          <StatCard title="This Month" value={`$${(rd.monthRevenue || 0).toFixed(2)}`} subtitle={`Subs $${(rd.monthSubs || 0).toFixed(0)} · Coins $${(rd.monthCoins || 0).toFixed(0)} · Tokens $${(rd.monthDiamonds || 0).toFixed(0)} · Streak $${(rd.monthStreak || 0).toFixed(0)}`} />
          <StatCard title="Registrations" value={rd.todayRegistrations || 0} subtitle="New users today" />
          <StatCard title="Trial Starts" value={trialMap['INITIAL_PURCHASE']?.count || 0} subtitle="Free trials started" />
          <StatCard title="Trial Converted" value={trialMap['RENEWAL']?.count || 0} subtitle={`$${(trialMap['RENEWAL']?.revenue || 0).toFixed(2)}`} />
          <StatCard title="Quiz Done" value={quizStats.completed || 0} subtitle={`${quizStats.skipped || 0} skipped`} />
        </div>

        {/* ── Daily Revenue + Trial Starts Chart ── */}
        <ChartCard title="Daily Revenue & Trial Starts (30 days)">
          <ResponsiveContainer width="100%" height={350}>
            <ComposedChart data={dailyRev}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="date" stroke="#64748b" fontSize={11} tickLine={false} tickFormatter={(v: string) => v.slice(5)} />
              <YAxis yAxisId="revenue" stroke="#64748b" fontSize={11} tickLine={false} tickFormatter={(v: number) => `$${v}`} />
              <YAxis yAxisId="count" orientation="right" stroke="#64748b" fontSize={11} tickLine={false} />
              <Tooltip cursor={{ fill: 'rgba(255,255,255,0.05)' }} content={({ active, payload, label }: any) => {
                if (!active || !payload?.length) return null;
                const d = payload[0]?.payload || {};
                return (
                  <div style={{ ...tooltipStyle, padding: '10px 14px', fontSize: '13px' }}>
                    <p style={{ fontWeight: 600, marginBottom: 6 }}>{label}</p>
                    <p style={{ color: '#4ade80' }}>Subscriptions: ${Number(d.subs || 0).toFixed(2)}</p>
                    <p style={{ color: '#FF9C15' }}>Coins: ${Number(d.coins || 0).toFixed(2)}</p>
                    {(d.diamonds || 0) > 0 && <p style={{ color: '#c084fc' }}>Tokens: ${Number(d.diamonds).toFixed(2)}</p>}
                    {(d.streak || 0) > 0 && <p style={{ color: '#38bdf8' }}>Streak: ${Number(d.streak).toFixed(2)}</p>}
                    <p style={{ color: '#fff', fontWeight: 700, marginTop: 4, borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: 4 }}>Total: ${Number(d.total || 0).toFixed(2)}</p>
                    <div style={{ marginTop: 6, borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 6 }}>
                      <p style={{ color: '#c084fc' }}>Trial Starts: {d.trialStarts || 0}</p>
                      <p style={{ color: '#38bdf8' }}>Trial → Paid: {d.trialRenewals || 0}</p>
                      <p style={{ color: '#f87171' }}>Cancellations: {d.allCancels || 0}</p>
                    </div>
                  </div>
                );
              }} />
              <Legend />
              <Bar yAxisId="revenue" dataKey="subs" fill="#4ade80" name="Sub Revenue" stackId="a" />
              <Bar yAxisId="revenue" dataKey="coins" fill="#FF9C15" name="Coin Revenue" stackId="a" />
              <Bar yAxisId="revenue" dataKey="diamonds" fill="#c084fc" name="Token Revenue" stackId="a" />
              <Bar yAxisId="revenue" dataKey="streak" fill="#38bdf8" name="Streak Revenue" stackId="a" />
              <Line yAxisId="count" type="monotone" dataKey="trialStarts" stroke="#c084fc" strokeWidth={2} dot={false} name="Trial Starts" />
              <Line yAxisId="count" type="monotone" dataKey="trialRenewals" stroke="#38bdf8" strokeWidth={2} dot={false} name="Trial → Paid" />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* ── Trial & Subscription Lifecycle (30d) ── */}
        <ChartCard title="Trial & Subscription Lifecycle (30 days)">
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={dailyRev}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="date" stroke="#64748b" fontSize={11} tickLine={false} tickFormatter={(v: string) => v.slice(5)} />
              <YAxis stroke="#64748b" fontSize={11} tickLine={false} />
              <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }} />
              <Legend />
              <Bar dataKey="trialStarts" fill="rgba(192,132,252,0.3)" stroke="#c084fc" name="Trial Starts" />
              <Bar dataKey="allPurchases" fill="rgba(255,156,21,0.3)" stroke="#FF9C15" name="All Purchases" />
              <Line type="monotone" dataKey="trialRenewals" stroke="#4ade80" strokeWidth={2.5} dot={{ r: 3, fill: '#4ade80' }} name="Trial → Paid" />
              <Line type="monotone" dataKey="allRenewals" stroke="#38bdf8" strokeWidth={2} dot={false} name="All Renewals" />
              <Line type="monotone" dataKey="allCancels" stroke="#f87171" strokeWidth={2} dot={false} name="Cancellations" />
              <Line type="monotone" dataKey="billingIssues" stroke="#fbbf24" strokeWidth={1.5} strokeDasharray="5 5" dot={false} name="Billing Issues" />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* ── Variant Funnel ── */}
        <ChartCard title="Variant A/B/C Funnel (Today)">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left text-gray-400 py-2 px-3">Metric</th>
                  {variants.map(v => <th key={v} className="text-right text-gray-400 py-2 px-3 capitalize">{v} ({v === 'trial' ? 'A' : v === 'simple' ? 'B' : 'C'})</th>)}
                </tr>
              </thead>
              <tbody>
                {funnelEvents.map(e => (
                  <tr key={e} className="border-b border-white/5">
                    <td className="text-gray-300 py-2 px-3">{eventLabels[e]}</td>
                    {variants.map(v => {
                      const val = vfMap[v]?.[e] || 0;
                      const views = vfMap[v]?.['trial_paywall_view'] || 0;
                      const pct = views > 0 ? ` (${((val / views) * 100).toFixed(0)}%)` : '';
                      const color = e.includes('success') ? 'text-green-400' : e.includes('fail') ? 'text-red-400' : e.includes('close') ? 'text-yellow-400' : 'text-white';
                      return <td key={v} className={`text-right py-2 px-3 font-mono ${color}`}>{formatNumber(val)}{e !== 'trial_paywall_view' ? <span className="text-gray-500 text-xs">{pct}</span> : ''}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ChartCard>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* ── Today by Product ── */}
          <ChartCard title="Today Revenue by Product">
            {todayProducts.length > 0 ? (
              <div className="space-y-3 p-2">
                {todayProducts.map((p: any, i: number) => (
                  <div key={i} className="flex justify-between items-center text-sm">
                    <span className="text-gray-400">{p.product}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-gray-500">{p.users} users</span>
                      <span className="text-green-400 font-semibold">${p.revenue.toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-gray-500 text-center py-8">No revenue yet today</p>}
          </ChartCard>

          {/* ── Today by Country ── */}
          <ChartCard title="Today Revenue by Country">
            {todayCountries.length > 0 ? (
              <div className="space-y-3 p-2">
                {todayCountries.slice(0, 10).map((c: any, i: number) => (
                  <div key={i} className="flex justify-between items-center text-sm">
                    <span className="text-gray-400">{c.country}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-gray-500">{c.users} users</span>
                      <span className="text-green-400 font-semibold">${c.revenue.toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-gray-500 text-center py-8">No revenue yet today</p>}
          </ChartCard>
        </div>

        {/* ── Source Performance ── */}
        <ChartCard title="Paywall Source Performance (Today)">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left text-gray-400 py-2 px-3">Source</th>
                  <th className="text-right text-gray-400 py-2 px-3">Views</th>
                  <th className="text-right text-gray-400 py-2 px-3">Checkout</th>
                  <th className="text-right text-gray-400 py-2 px-3">Conv%</th>
                  <th className="text-right text-gray-400 py-2 px-3">Close%</th>
                </tr>
              </thead>
              <tbody>
                {sortedSources.map((s, i) => (
                  <tr key={i} className="border-b border-white/5">
                    <td className="text-gray-300 py-2 px-3">{s.source}</td>
                    <td className="text-right text-white py-2 px-3 font-mono">{s.views}</td>
                    <td className="text-right text-white py-2 px-3 font-mono">{s.checkout}</td>
                    <td className={`text-right py-2 px-3 font-mono ${s.views > 0 && (s.checkout / s.views) > 0.15 ? 'text-green-400' : 'text-red-400'}`}>{s.views > 0 ? ((s.checkout / s.views) * 100).toFixed(0) : 0}%</td>
                    <td className="text-right text-yellow-400 py-2 px-3 font-mono">{s.views > 0 ? ((s.close / s.views) * 100).toFixed(0) : 0}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ChartCard>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* ── Checkout Timing ── */}
          <ChartCard title="Time to Checkout (Today)">
            <div className="space-y-3 p-2">
              {checkoutTiming.map((t: any, i: number) => (
                <div key={i} className="flex justify-between items-center text-sm">
                  <span className="text-gray-400 capitalize">{t.variant}</span>
                  <div className="flex items-center gap-4">
                    <span className="text-gray-500">{t.count} checkouts</span>
                    <span className="text-white font-mono">med {t.medianSec}s</span>
                    <span className="text-gray-500 font-mono">avg {t.avgSec}s</span>
                  </div>
                </div>
              ))}
              {checkoutTiming.length === 0 && <p className="text-gray-500 text-center py-4">No timing data yet</p>}
            </div>
          </ChartCard>

          {/* ── Close Button Timing ── */}
          <ChartCard title="Time to Close (Today)">
            <div className="space-y-3 p-2">
              {closeTimingData.map((c: any, i: number) => (
                <div key={i} className="flex justify-between items-center text-sm">
                  <span className="text-gray-400 capitalize">{c.variant}</span>
                  <div className="flex items-center gap-4">
                    <span className="text-gray-500">{c.count} closes</span>
                    <span className="text-white font-mono">med {c.medianSec}s</span>
                    <span className="text-gray-500 font-mono">avg {c.avgSec}s</span>
                  </div>
                </div>
              ))}
              {closeTimingData.length === 0 && <p className="text-gray-500 text-center py-4">No timing data yet</p>}
            </div>
          </ChartCard>
        </div>

        {/* ── Trial Funnel Today ── */}
        <ChartCard title="Trial Funnel (Today)">
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 p-2">
            <div className="text-center"><p className="text-2xl font-bold text-white">{trialMap['INITIAL_PURCHASE']?.count || 0}</p><p className="text-xs text-gray-400">Starts</p></div>
            <div className="text-center"><p className="text-2xl font-bold text-red-400">{trialMap['CANCELLATION']?.count || 0}</p><p className="text-xs text-gray-400">Cancelled</p></div>
            <div className="text-center"><p className="text-2xl font-bold text-yellow-400">{trialMap['BILLING_ISSUE']?.count || 0}</p><p className="text-xs text-gray-400">Billing Issue</p></div>
            <div className="text-center"><p className="text-2xl font-bold text-orange-400">{trialMap['EXPIRATION']?.count || 0}</p><p className="text-xs text-gray-400">Expired</p></div>
            <div className="text-center"><p className="text-2xl font-bold text-green-400">{trialMap['RENEWAL']?.count || 0}</p><p className="text-xs text-gray-400">Converted</p></div>
            <div className="text-center"><p className="text-2xl font-bold text-blue-400">{trialMap['PRODUCT_CHANGE']?.count || 0}</p><p className="text-xs text-gray-400">Plan Change</p></div>
          </div>
        </ChartCard>

        {/* ── Recent Orders (Live Feed) ── */}
        <ChartCard title="Recent Orders (Live)">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left text-gray-400 py-2 px-3">Order Date</th>
                  <th className="text-left text-gray-400 py-2 px-3">Cleared</th>
                  <th className="text-left text-gray-400 py-2 px-3">Product</th>
                  <th className="text-left text-gray-400 py-2 px-3">Type</th>
                  <th className="text-left text-gray-400 py-2 px-3">Store</th>
                  <th className="text-left text-gray-400 py-2 px-3">Country</th>
                  <th className="text-left text-gray-400 py-2 px-3">User ID</th>
                  <th className="text-left text-gray-400 py-2 px-3">Order ID</th>
                  <th className="text-right text-gray-400 py-2 px-3">Price</th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.map((o: any, i: number) => (
                  <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                    <td className="text-gray-500 py-2 px-3 font-mono text-xs">{o.orderedAt ? new Date(o.orderedAt).toLocaleString() : '—'}</td>
                    <td className="text-gray-500 py-2 px-3 font-mono text-xs">{o.clearedAt ? new Date(o.clearedAt).toLocaleString() : '—'}</td>
                    <td className="text-white py-2 px-3">{o.product}</td>
                    <td className="py-2 px-3"><span className={`text-xs px-2 py-0.5 rounded-full ${o.type === 'RENEWAL' ? 'bg-green-500/10 text-green-400' : o.type === 'INITIAL_PURCHASE' ? 'bg-blue-500/10 text-blue-400' : 'bg-orange-500/10 text-orange-400'}`}>{o.type.replace('_', ' ')}</span></td>
                    <td className="py-2 px-3"><span className={`text-xs px-2 py-0.5 rounded-full ${o.store === 'Android' ? 'bg-green-500/10 text-green-400' : 'bg-blue-500/10 text-blue-400'}`}>{o.store || '?'}</span></td>
                    <td className="text-gray-400 py-2 px-3">{o.country}</td>
                    <td className="text-gray-500 py-2 px-3 font-mono text-xs">
                      {o.userId ? (
                        <button className="hover:text-white transition-colors cursor-pointer text-left" onClick={() => { navigator.clipboard.writeText(o.userId); }} title="Click to copy">{o.userId.startsWith('$RC') ? <span className="text-red-400">RC Anonymous</span> : o.userId}</button>
                      ) : '—'}
                    </td>
                    <td className="text-gray-500 py-2 px-3 font-mono text-xs">
                      {o.orderId ? (
                        <button className="hover:text-white transition-colors cursor-pointer text-left" onClick={() => { navigator.clipboard.writeText(o.orderId); }} title="Click to copy">{o.orderId}</button>
                      ) : '—'}
                    </td>
                    <td className="text-right text-green-400 py-2 px-3 font-mono">${o.price?.toFixed(2) || '0.00'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ChartCard>
      </div>
    );
  };

  // ─── User Journey Tab ─────────────────────────────────
  const renderUserSegments = () => {
    const seg = data['user-segments'] || {};
    const segmentStats: any[] = seg.segmentStats || [];
    const combos: any[] = seg.featureCombinations || [];
    const powerUsers: any[] = seg.powerUsers || [];
    const correlations: any[] = seg.retentionCorrelations || [];
    const meta = seg.meta || {};

    const segmentLabels: Record<string, string> = {
      feed_only: 'Feed Only', creator: 'Creator', ai_lens: 'AI Lens',
      health_tracker: 'Health Tracker', kundli: 'Palm Kundli', im_power: 'IM Power User',
      leaderboard: 'Leaderboard', multi_feature: 'Multi-Feature', casual: 'Casual', ai_chat: 'Ask AI',
    };

    if (!seg.segmentStats) {
      return (
        <div className="space-y-6">
          <div className="p-8 bg-white/5 rounded-xl text-center">
            <p className="text-gray-400 text-lg mb-2">No segment analysis data yet</p>
            <p className="text-gray-500 text-sm">Run the segment analysis job to generate data:</p>
            <code className="text-xs text-gray-500 mt-2 block">kubectl apply -f server/k8s/segment-analysis-job.yaml</code>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Meta */}
        <div className="flex items-center gap-4 text-xs text-gray-500">
          <span>Users analyzed: {formatNumber(meta.totalUsersAnalyzed || 0)}</span>
          <span>Window: {meta.analysisWindow}</span>
          <span>Generated: {meta.generatedAt ? new Date(meta.generatedAt).toLocaleString() : '—'}</span>
        </div>

        {/* ── Segment Comparison Table ── */}
        <ChartCard title="User Segment Comparison (30 days)">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-400 border-b border-white/10">
                  <th className="text-left py-2 px-3">Segment</th>
                  <th className="text-right py-2 px-3">Users</th>
                  <th className="text-right py-2 px-3">Avg Days</th>
                  <th className="text-right py-2 px-3">Avg Sessions</th>
                  <th className="text-right py-2 px-3">Avg Duration</th>
                  <th className="text-right py-2 px-3">D1 Ret</th>
                  <th className="text-right py-2 px-3">D7 Ret</th>
                  <th className="text-right py-2 px-3">D30 Ret</th>
                </tr>
              </thead>
              <tbody>
                {segmentStats.map((s: any) => (
                  <tr key={s.segment} className="border-b border-white/5 hover:bg-white/5">
                    <td className="py-2 px-3 text-white font-medium">{segmentLabels[s.segment] || s.segment}</td>
                    <td className="py-2 px-3 text-right text-gray-300">{formatNumber(s.userCount)}</td>
                    <td className="py-2 px-3 text-right text-gray-300">{s.avgDaysActive}</td>
                    <td className="py-2 px-3 text-right text-gray-300">{s.avgSessionsPerUser}</td>
                    <td className="py-2 px-3 text-right text-gray-300">{s.avgSessionDurationMin > 0 ? `${s.avgSessionDurationMin}m` : '—'}</td>
                    <td className="py-2 px-3 text-right">
                      <span className={s.retentionD1 > 30 ? 'text-green-400' : s.retentionD1 > 15 ? 'text-yellow-400' : 'text-red-400'}>{s.retentionD1}%</span>
                    </td>
                    <td className="py-2 px-3 text-right">
                      <span className={`font-bold ${s.retentionD7 > 20 ? 'text-green-400' : s.retentionD7 > 10 ? 'text-yellow-400' : 'text-red-400'}`}>{s.retentionD7}%</span>
                    </td>
                    <td className="py-2 px-3 text-right">
                      <span className={s.retentionD30 > 10 ? 'text-green-400' : s.retentionD30 > 5 ? 'text-yellow-400' : 'text-red-400'}>{s.retentionD30}%</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ChartCard>

        {/* ── Segment Size Bar Chart ── */}
        <ChartCard title="Segment Distribution">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={segmentStats.map((s: any) => ({ name: segmentLabels[s.segment] || s.segment, users: s.userCount, d7: s.retentionD7 }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="name" stroke="#888" fontSize={11} angle={-20} textAnchor="end" height={60} />
              <YAxis yAxisId="left" stroke="#888" fontSize={12} />
              <YAxis yAxisId="right" orientation="right" stroke="#34d399" fontSize={12} unit="%" />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend />
              <Bar yAxisId="left" dataKey="users" fill="#FF9C15" radius={[6, 6, 0, 0]} name="Users" />
              <Bar yAxisId="right" dataKey="d7" fill="#34d399" radius={[6, 6, 0, 0]} name="D7 Retention %" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* ── Feature → Retention Correlation ── */}
        <ChartCard title="Feature Impact on D7 Retention (Lift)">
          {correlations.length > 0 ? (
            <div className="space-y-3">
              {correlations.map((c: any) => {
                const barWidth = Math.min(c.lift * 30, 100);
                return (
                  <div key={c.feature} className="flex items-center gap-3">
                    <div className="w-44 text-xs text-gray-400 text-right truncate">{c.feature}</div>
                    <div className="flex-1 flex items-center gap-2">
                      <div className="flex-1 h-7 bg-white/5 rounded-lg overflow-hidden relative">
                        <div className="h-full rounded-lg transition-all" style={{
                          width: `${barWidth}%`,
                          background: c.lift >= 2 ? 'linear-gradient(to right, #34d399, #22c55e)' : c.lift >= 1.3 ? 'linear-gradient(to right, #FF9C15, #fbbf24)' : 'linear-gradient(to right, #ef4444, #f87171)',
                        }} />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-white font-bold">{c.lift}x lift</span>
                      </div>
                      <div className="w-32 text-[10px] text-gray-500">
                        {c.retD7With}% vs {c.retD7Without}%
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : <p className="text-gray-500 text-sm">No correlation data</p>}
        </ChartCard>

        {/* ── Top Feature Combinations ── */}
        <ChartCard title="Best Feature Combinations for Retention">
          {combos.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-gray-400 border-b border-white/10">
                    <th className="text-left py-2 px-3">Features</th>
                    <th className="text-right py-2 px-3">Users</th>
                    <th className="text-right py-2 px-3">Avg Days</th>
                    <th className="text-right py-2 px-3">Avg Sessions</th>
                    <th className="text-right py-2 px-3">D7 Ret</th>
                    <th className="text-right py-2 px-3">D30 Ret</th>
                  </tr>
                </thead>
                <tbody>
                  {combos.slice(0, 20).map((c: any, i: number) => (
                    <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                      <td className="py-2 px-3 text-white font-medium">{c.features.join(' + ')}</td>
                      <td className="py-2 px-3 text-right text-gray-300">{formatNumber(c.userCount)}</td>
                      <td className="py-2 px-3 text-right text-gray-300">{c.avgDaysActive}</td>
                      <td className="py-2 px-3 text-right text-gray-300">{c.avgSessions}</td>
                      <td className="py-2 px-3 text-right">
                        <span className={`font-bold ${c.retentionD7 > 20 ? 'text-green-400' : c.retentionD7 > 10 ? 'text-yellow-400' : 'text-red-400'}`}>{c.retentionD7}%</span>
                      </td>
                      <td className="py-2 px-3 text-right">
                        <span className={c.retentionD30 > 10 ? 'text-green-400' : 'text-red-400'}>{c.retentionD30}%</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="text-gray-500 text-sm">No combination data</p>}
        </ChartCard>

        {/* ── Power User Tiers ── */}
        <ChartCard title="Power User Tiers">
          {powerUsers.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {powerUsers.map((p: any) => (
                <div key={p.tier} className="p-4 bg-white/5 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-bold text-white">{p.tier}</span>
                    <span className="text-lg font-bold text-[#FF9C15]">{formatNumber(p.count)}</span>
                  </div>
                  <div className="text-xs text-gray-400 mb-3">
                    Avg {p.avgFeatures} features | {p.avgSessions} sessions | {p.avgDaysActive} days
                  </div>
                  <div className="space-y-1">
                    {(p.topFeatures || []).map((f: any) => (
                      <div key={f.feature} className="flex items-center justify-between text-xs">
                        <span className="text-gray-300">{f.feature}</span>
                        <div className="flex items-center gap-2">
                          <div className="w-20 h-2 bg-white/10 rounded-full overflow-hidden">
                            <div className="h-full bg-[#FF9C15] rounded-full" style={{ width: `${f.pct}%` }} />
                          </div>
                          <span className="text-gray-400 w-10 text-right">{f.pct}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-gray-500 text-sm">No power user data</p>}
        </ChartCard>
      </div>
    );
  };

  const renderUserJourney = () => {
    const journey = data['user-journey'] || {};
    const signupFunnel: any[] = journey.signupFunnel || [];
    const journeyDetailFunnel: any[] = journey.journeyDetailFunnel || [];
    const leaderboardFunnel: any[] = journey.leaderboardFunnel || [];
    const aiLensFunnel: any[] = journey.aiLensFunnel || [];
    const quizGoals: any[] = journey.quizGoals || [];
    const quizSteps: any[] = journey.quizSteps || [];
    const destinations: any[] = journey.destinations || [];
    const paywallPages: any[] = journey.paywallPages || [];
    const paywallSkips: any[] = journey.paywallSkips || [];
    const dailyJourney: any[] = journey.dailyJourney || [];
    const quizSkips: any[] = journey.quizSkips || [];
    const followDetail: any = journey.followDetail || [];
    const paywallErrors: any[] = journey.paywallErrors || [];
    const paywallSkipDetail: any[] = journey.paywallSkipDetail || [];
    const paywallConversionBySource: any[] = journey.paywallConversionBySource || [];

    // Build signup funnel with drop-off %
    const signupFunnelWithDropoff = signupFunnel.map((s: any, i: number) => {
      const prev = i > 0 ? signupFunnel[i - 1] : null;
      const dropoff = prev && prev.users > 0 ? ((1 - s.users / prev.users) * 100).toFixed(1) : null;
      return { ...s, dropoff };
    });

    // Build journey detail funnel with drop-off %
    const journeyDetailWithDropoff = journeyDetailFunnel.map((s: any, i: number) => {
      const prev = i > 0 ? journeyDetailFunnel[i - 1] : null;
      const dropoff = prev && prev.users > 0 ? ((1 - s.users / prev.users) * 100).toFixed(1) : null;
      return { ...s, dropoff };
    });

    // Daily chart: pivot by event
    const dailyMap: Record<string, any> = {};
    for (const d of dailyJourney) {
      if (!dailyMap[d.day]) dailyMap[d.day] = { day: formatDate(d.day) };
      dailyMap[d.day][d.event] = d.uniqueUsers;
    }
    const dailyChart = Object.values(dailyMap).sort((a: any, b: any) => a.day.localeCompare(b.day));

    // Paywall page funnel by source
    const signupPages = paywallPages.filter((p: any) => p.source === 'signup').sort((a: any, b: any) => a.page - b.page);

    // Quiz skip total
    const totalQuizSkips = quizSkips.reduce((sum: number, s: any) => sum + s.count, 0);

    // Pivot paywallConversionBySource into per-source rows
    const conversionMap: Record<string, any> = {};
    for (const r of paywallConversionBySource) {
      if (!conversionMap[r.source]) conversionMap[r.source] = { source: r.source, views: 0, checkouts: 0, success: 0, fails: 0, skips: 0 };
      const row = conversionMap[r.source];
      if (r.event === 'trial_paywall_view') row.views = r.uniqueUsers;
      else if (r.event === 'trial_paywall_checkout_start') row.checkouts = r.uniqueUsers;
      else if (r.event === 'trial_paywall_checkout_success') row.success = r.uniqueUsers;
      else if (r.event === 'trial_paywall_checkout_fail') row.fails = r.uniqueUsers;
      else if (r.event === 'journey_paywall_skip') row.skips = r.uniqueUsers;
    }
    const conversionRows = Object.values(conversionMap).sort((a: any, b: any) => b.views - a.views);

    // Normalize followDetail to array
    const followDetailArr = Array.isArray(followDetail) ? followDetail :
      [{ action: 'completed', ...followDetail.completed }, { action: 'skipped', ...followDetail.skipped }].filter((f: any) => f.count > 0 || f.uniqueUsers > 0);

    return (
      <div className="space-y-6">
        {/* Top stats — use paywall funnel (reliable old events) */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard title="Onboarded" value={signupFunnel.find((s: any) => s.step === 'username_form')?.users || 0} subtitle="Username form (30d)" />
          <StatCard title="Paywall Viewed" value={signupFunnel.find((s: any) => s.step === 'paywall_view')?.users || 0} subtitle="Saw trial paywall" />
          <StatCard title="Checkout Started" value={signupFunnel.find((s: any) => s.step === 'paywall_checkout')?.users || 0} subtitle="Started payment" />
          <StatCard title="Subscribed" value={signupFunnel.find((s: any) => s.step === 'paywall_success')?.users || 0}
            subtitle={`${signupFunnel.find((s: any) => s.step === 'paywall_view')?.users ? ((signupFunnel.find((s: any) => s.step === 'paywall_success')?.users / signupFunnel.find((s: any) => s.step === 'paywall_view')?.users) * 100).toFixed(1) : 0}% of views`} />
          <StatCard title="Skipped Paywall" value={signupFunnel.find((s: any) => s.step === 'paywall_skip')?.users || 0} subtitle="Closed paywall" />
        </div>

        {/* ── Paywall Conversion Funnel (reliable data) ── */}
        <ChartCard title="Paywall Conversion Funnel (30 days)">
          <div className="space-y-2">
            {signupFunnelWithDropoff.map((step: any) => {
              const maxUsers = signupFunnel[0]?.users || 1;
              const pct = maxUsers > 0 ? (step.users / maxUsers) * 100 : 0;
              return (
                <div key={step.step} className="flex items-center gap-3">
                  <div className="w-40 text-xs text-gray-400 text-right truncate">{step.label}</div>
                  <div className="flex-1 h-8 bg-white/5 rounded-lg overflow-hidden relative">
                    <div className="h-full bg-gradient-to-r from-[#FF9C15] to-[#F42026] rounded-lg transition-all" style={{ width: `${Math.max(pct, 1)}%` }} />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-white font-medium">
                      {formatNumber(step.users)} users
                    </span>
                  </div>
                  <div className="w-16 text-xs text-right">
                    {step.dropoff ? <span className="text-red-400">-{step.dropoff}%</span> : <span className="text-gray-600">--</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </ChartCard>

        {/* ── Detailed Journey Funnel (new tracking — growing as users update) ── */}
        <ChartCard title="Detailed Journey Funnel (new app versions only)">
          {journeyDetailFunnel.some((s: any) => s.users > 0) ? (
            <div className="space-y-2">
              {journeyDetailWithDropoff.map((step: any) => {
                const maxUsers = journeyDetailFunnel[0]?.users || 1;
                const pct = maxUsers > 0 ? (step.users / maxUsers) * 100 : 0;
                return (
                  <div key={step.step} className="flex items-center gap-3">
                    <div className="w-40 text-xs text-gray-400 text-right truncate">{step.label}</div>
                    <div className="flex-1 h-8 bg-white/5 rounded-lg overflow-hidden relative">
                      <div className="h-full bg-gradient-to-r from-[#38bdf8] to-[#818cf8] rounded-lg transition-all" style={{ width: `${Math.max(pct, 1)}%` }} />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-white font-medium">
                        {formatNumber(step.users)} users
                      </span>
                    </div>
                    <div className="w-16 text-xs text-right">
                      {step.dropoff ? <span className="text-red-400">-{step.dropoff}%</span> : <span className="text-gray-600">--</span>}
                    </div>
                  </div>
                );
              })}
              <p className="text-xs text-gray-500 mt-2">Note: These events only fire from new app versions. Numbers will grow as users update.</p>
            </div>
          ) : <p className="text-gray-500 text-sm">Waiting for users on new app version</p>}
        </ChartCard>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* ── Quiz Goal Distribution ── */}
          <ChartCard title="Quiz Primary Goal Distribution">
            {quizGoals.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie data={quizGoals.map((g: any) => ({ name: g.goal || 'unknown', value: g.count }))} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, percent }: any) => `${name} ${(percent * 100).toFixed(0)}%`}>
                    {quizGoals.map((_: any, i: number) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: 'rgba(15,23,42,0.95)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', color: '#fff' }} />
                </PieChart>
              </ResponsiveContainer>
            ) : <p className="text-gray-500 text-sm">No data yet</p>}
          </ChartCard>

          {/* ── First Destination Distribution ── */}
          <ChartCard title="First Destination After Onboarding">
            {destinations.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie data={destinations.map((d: any) => ({ name: d.destination || 'unknown', value: d.count }))} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, percent }: any) => `${name} ${(percent * 100).toFixed(0)}%`}>
                    {destinations.map((_: any, i: number) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: 'rgba(15,23,42,0.95)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', color: '#fff' }} />
                </PieChart>
              </ResponsiveContainer>
            ) : <p className="text-gray-500 text-sm">No data yet</p>}
          </ChartCard>
        </div>

        {/* ── Quiz Skip Breakdown ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChartCard title={`Quiz Skip by Step (${formatNumber(totalQuizSkips)} total skips)`}>
            {quizSkips.length > 0 ? (
              <div className="space-y-2">
                {quizSkips.map((s: any) => {
                  const maxCount = quizSkips[0]?.count || 1;
                  const pct = (s.count / maxCount) * 100;
                  return (
                    <div key={s.stepId} className="flex items-center gap-2">
                      <div className="w-28 text-xs text-gray-400 truncate text-right" title={s.stepId}>{s.stepId}</div>
                      <div className="flex-1 h-6 bg-white/5 rounded overflow-hidden relative">
                        <div className="h-full bg-red-500/60 rounded" style={{ width: `${pct}%` }} />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-white">
                          step {s.stepIndex}/{s.totalSteps} — {formatNumber(s.count)} skips ({formatNumber(s.uniqueUsers)} users)
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : <p className="text-gray-500 text-sm">No quiz skip data yet</p>}
          </ChartCard>

          {/* ── Quiz Step Engagement ── */}
          <ChartCard title="Quiz Step Engagement (Top Steps)">
            {quizSteps.length > 0 ? (
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {quizSteps.slice(0, 15).map((s: any) => {
                  const maxCount = quizSteps[0]?.uniqueUsers || 1;
                  const pct = (s.uniqueUsers / maxCount) * 100;
                  return (
                    <div key={s.stepId} className="flex items-center gap-2">
                      <div className="w-32 text-xs text-gray-400 truncate text-right" title={s.stepId}>{s.stepId}</div>
                      <div className="flex-1 h-5 bg-white/5 rounded overflow-hidden">
                        <div className="h-full bg-[#FF9C15]/60 rounded" style={{ width: `${pct}%` }} />
                      </div>
                      <div className="w-12 text-xs text-gray-300 text-right">{s.uniqueUsers}</div>
                    </div>
                  );
                })}
              </div>
            ) : <p className="text-gray-500 text-sm">No data yet</p>}
          </ChartCard>
        </div>

        {/* ── Paywall Page Drop-off (Signup Flow) ── */}
        <ChartCard title="Trial Paywall Page Drop-off (Signup Flow)">
          {signupPages.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={signupPages.map((p: any) => ({ name: p.page === 0 ? 'Features' : p.page === 1 ? 'Plans' : `Page ${p.page}`, users: p.uniqueUsers, events: p.count }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="name" stroke="#888" fontSize={12} />
                <YAxis stroke="#888" fontSize={12} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="users" fill="#FF9C15" radius={[6, 6, 0, 0]} name="Unique Users" />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-gray-500 text-sm">No data yet — paywall page tracking will appear after deployment</p>}
        </ChartCard>

        {/* ── Paywall Conversion by Source ── */}
        <ChartCard title="Paywall Conversion Rate by Source">
          {conversionRows.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-gray-400 border-b border-white/10">
                    <th className="text-left py-2 px-3">Source</th>
                    <th className="text-right py-2 px-3">Views</th>
                    <th className="text-right py-2 px-3">Checkouts</th>
                    <th className="text-right py-2 px-3">Success</th>
                    <th className="text-right py-2 px-3">Fails</th>
                    <th className="text-right py-2 px-3">Skips</th>
                    <th className="text-right py-2 px-3">Conv. Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {conversionRows.map((r: any) => {
                    const convRate = r.views > 0 ? ((r.success / r.views) * 100).toFixed(1) : '0.0';
                    return (
                      <tr key={r.source} className="border-b border-white/5 hover:bg-white/5">
                        <td className="py-2 px-3 text-white font-medium">{r.source}</td>
                        <td className="py-2 px-3 text-right text-gray-300">{formatNumber(r.views)}</td>
                        <td className="py-2 px-3 text-right text-gray-300">{formatNumber(r.checkouts)}</td>
                        <td className="py-2 px-3 text-right text-green-400">{formatNumber(r.success)}</td>
                        <td className="py-2 px-3 text-right text-red-400">{formatNumber(r.fails)}</td>
                        <td className="py-2 px-3 text-right text-yellow-400">{formatNumber(r.skips)}</td>
                        <td className="py-2 px-3 text-right">
                          <span className={`font-bold ${parseFloat(convRate) > 5 ? 'text-green-400' : parseFloat(convRate) > 1 ? 'text-yellow-400' : 'text-red-400'}`}>
                            {convRate}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : <p className="text-gray-500 text-sm">No conversion data yet</p>}
        </ChartCard>

        {/* ── Paywall Skip Detail + Paywall Errors ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChartCard title="Paywall Skip Detail by Source">
            {paywallSkipDetail.length > 0 ? (
              <div className="space-y-2">
                {paywallSkipDetail.map((s: any) => (
                  <div key={s.source} className="p-3 bg-white/5 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-white">{s.source}</span>
                      <span className="text-sm text-gray-300">{formatNumber(s.uniqueUsers)} users</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-400">
                      <span>Page: {s.avgPage?.toFixed(1) ?? '—'}</span>
                      <span>Avg time: {s.avgTimeSpentMs ? `${(s.avgTimeSpentMs / 1000).toFixed(1)}s` : '—'}</span>
                      <span>{formatNumber(s.count)} skips</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-gray-500 text-sm">No skip detail yet</p>}
          </ChartCard>

          <ChartCard title="Payment Errors">
            {paywallErrors.length > 0 ? (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {paywallErrors.map((e: any, i: number) => (
                  <div key={i} className="p-3 bg-white/5 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-red-400">{e.errorCode || 'unknown'}</span>
                      <span className="text-sm text-white font-bold">{formatNumber(e.count)} errors</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-400">
                      <span>Plan: {e.plan || '—'}</span>
                      <span>Source: {e.source || '—'}</span>
                      <span>{formatNumber(e.uniqueUsers)} users affected</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-gray-500 text-sm">No payment errors (good!)</p>}
          </ChartCard>
        </div>

        {/* ── Follow Step Detail ── */}
        <ChartCard title="Mutual Friends Follow Step">
          {followDetailArr.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {followDetailArr.map((f: any) => (
                <div key={f.action} className="p-4 bg-white/5 rounded-xl text-center">
                  <div className="text-2xl font-bold text-white mb-1">{formatNumber(f.uniqueUsers)}</div>
                  <div className="text-sm text-gray-400 mb-2">{f.action === 'completed' ? 'Followed & Continued' : f.action === 'skipped' ? 'Skipped' : f.action}</div>
                  {f.avgFollowed != null && (
                    <div className="text-xs text-gray-500">Avg followed: {f.avgFollowed.toFixed(1)} people</div>
                  )}
                </div>
              ))}
            </div>
          ) : <p className="text-gray-500 text-sm">No follow step data yet</p>}
        </ChartCard>

        {/* ── Paywall Skip by Source (legacy simple view) ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChartCard title="Paywall Skips by Source (Simple)">
            {paywallSkips.length > 0 ? (
              <div className="space-y-2">
                {paywallSkips.map((s: any) => (
                  <div key={s.source} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <span className="text-sm text-gray-300">{s.source}</span>
                    <span className="text-sm font-medium text-white">{formatNumber(s.uniqueUsers)} users ({formatNumber(s.count)} events)</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-gray-500 text-sm">No data yet</p>}
          </ChartCard>

          {/* ── Leaderboard Onboarding Funnel ── */}
          <ChartCard title="Leaderboard Onboarding Funnel">
            {leaderboardFunnel.some((s: any) => s.users > 0) ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={leaderboardFunnel} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis type="number" stroke="#888" fontSize={12} />
                  <YAxis dataKey="label" type="category" stroke="#888" fontSize={12} width={120} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Bar dataKey="users" fill="#38bdf8" radius={[0, 6, 6, 0]} name="Users" />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-gray-500 text-sm">No data yet</p>}
          </ChartCard>
        </div>

        {/* ── AI Lens Onboarding Funnel ── */}
        <ChartCard title="AI Lens Onboarding Funnel">
          {aiLensFunnel.some((s: any) => s.users > 0) ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={aiLensFunnel} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis type="number" stroke="#888" fontSize={12} />
                <YAxis dataKey="label" type="category" stroke="#888" fontSize={12} width={120} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="users" fill="#34d399" radius={[0, 6, 6, 0]} name="Users" />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-gray-500 text-sm">No data yet</p>}
        </ChartCard>

        {/* ── Daily Journey Trends ── */}
        <ChartCard title="Daily Journey Trends (30 days)">
          {dailyChart.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={dailyChart}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="day" stroke="#888" fontSize={11} />
                <YAxis stroke="#888" fontSize={12} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend />
                <Line type="monotone" dataKey="journey_quiz_start" stroke="#FF9C15" strokeWidth={2} dot={false} name="Quiz Start" />
                <Line type="monotone" dataKey="journey_quiz_complete" stroke="#34d399" strokeWidth={2} dot={false} name="Quiz Complete" />
                <Line type="monotone" dataKey="journey_quiz_skip" stroke="#ef4444" strokeWidth={2} dot={false} name="Quiz Skip" />
                <Line type="monotone" dataKey="journey_destination" stroke="#38bdf8" strokeWidth={2} dot={false} name="Destination Reached" />
              </LineChart>
            </ResponsiveContainer>
          ) : <p className="text-gray-500 text-sm">No data yet</p>}
        </ChartCard>
      </div>
    );
  };

  const renderLeaderboard = () => {
    const lb = data['leaderboard-dashboard'] || {};
    const funnel = lb.funnel || [];
    const taskEng = lb.taskEngagement || [];
    const coinRev = lb.coinRevenue || [];
    const notifSt = lb.notifStats || [];
    const bannerP = lb.bannerPerf || [];
    const badgeD = lb.badgeDist || [];
    const trends = lb.lbDailyTrends || [];
    const actions = lb.actionsBreakdown || [];
    const prefs = lb.notifPrefs || {};

    // Build funnel stages
    const funnelStages = [
      { name: 'Joined', users: funnel.find((f: any) => f.event === 'journey_leaderboard_join')?.uniqueUsers || 0 },
      { name: 'Viewed Tasks', users: funnel.find((f: any) => f.event === 'tasks_view')?.uniqueUsers || 0 },
      { name: 'Viewed Leaderboard', users: funnel.find((f: any) => f.event === 'leaderboard_view')?.uniqueUsers || 0 },
      { name: 'Moved to Wallet', users: funnel.find((f: any) => f.event === 'leaderboard_action' && f.action === 'slide_to_wallet_complete')?.uniqueUsers || 0 },
      { name: 'Bought Coins', users: funnel.find((f: any) => f.event === 'coin_purchase_flow' && f.action === 'purchase_success')?.uniqueUsers || 0 },
    ];

    // Banner performance
    const bannerTypes = ['move_to_wallet', 'buy_coins_tier', 'plus_upsell', 'notification_enable'];
    const bannerData = bannerTypes.map(bt => ({
      name: bt.replace(/_/g, ' '),
      impressions: bannerP.find((b: any) => b.bannerType === bt && b.action === 'impression')?.count || 0,
      clicks: bannerP.find((b: any) => b.bannerType === bt && b.action === 'click')?.count || 0,
    }));

    // Notification effectiveness
    const notifTypes = ['overtake', 'daily_summary', 'badge_award', 'weekly'];
    const notifData = notifTypes.map(nt => ({
      name: nt.replace(/_/g, ' '),
      sent: notifSt.find((n: any) => n.notifType === nt && n.action === 'sent')?.count || 0,
      clicked: notifSt.find((n: any) => n.notifType === nt && (n.action === 'deeplink_click' || n.action === 'clicked'))?.count || 0,
      uniqueUsers: notifSt.find((n: any) => n.notifType === nt && (n.action === 'deeplink_click' || n.action === 'clicked'))?.uniqueUsers || 0,
    }));

    // Task completion
    const taskCompleted = taskEng.filter((t: any) => t.action === 'completed').sort((a: any, b: any) => b.count - a.count);
    const taskStarted = taskEng.filter((t: any) => t.action === 'started');

    // Compute totals for stat cards
    const totalJoined = funnelStages[0].users;
    const totalPurchases = coinRev.reduce((s: number, d: any) => s + d.purchases, 0);
    const totalRevenue = coinRev.reduce((s: number, d: any) => s + d.totalRevenue, 0);
    const totalBannerClicks = bannerData.reduce((s: number, d: any) => s + d.clicks, 0);

    return (
      <div className="space-y-6">
        {/* Stat Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard title="Users Joined" value={totalJoined} subtitle="30 days" />
          <StatCard title="Coin Purchases" value={totalPurchases} subtitle="30 days" />
          <StatCard title="Coin Revenue" value={`$${totalRevenue.toFixed(2)}`} subtitle="30 days" />
          <StatCard title="Banner Clicks" value={totalBannerClicks} subtitle="30 days" />
        </div>

        {/* Funnel */}
        <ChartCard title="Leaderboard Funnel (30d)">
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={funnelStages} layout="vertical">
              <XAxis type="number" stroke="#666" />
              <YAxis type="category" dataKey="name" width={120} stroke="#999" tick={{ fill: '#999', fontSize: 12 }} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="users" fill="#FF9C15" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Banner Performance + Task Engagement */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ChartCard title="Smart Banner Performance">
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={bannerData}>
                <XAxis dataKey="name" stroke="#666" tick={{ fill: '#999', fontSize: 10 }} />
                <YAxis stroke="#666" />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="impressions" fill="#FF9C15" name="Impressions" />
                <Bar dataKey="clicks" fill="#F42026" name="Clicks" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Top Tasks Completed">
            <div className="space-y-2 max-h-[250px] overflow-y-auto">
              {taskCompleted.slice(0, 10).map((t: any, i: number) => {
                const started = taskStarted.find((s: any) => s.taskType === t.taskType)?.count || t.count;
                const rate = started > 0 ? ((t.count / started) * 100).toFixed(0) : '0';
                return (
                  <div key={i} className="flex justify-between items-center py-2 border-b border-white/5">
                    <span className="text-sm text-gray-300">{t.taskType || 'unknown'}</span>
                    <div className="flex gap-3">
                      <span className="text-xs text-gray-500">{formatNumber(t.count)} done</span>
                      <span className="text-xs font-medium text-emerald-400">{rate}%</span>
                    </div>
                  </div>
                );
              })}
              {taskCompleted.length === 0 && <p className="text-gray-500 text-sm">No task data yet</p>}
            </div>
          </ChartCard>
        </div>

        {/* Coin Revenue + Notification Effectiveness */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ChartCard title="Coin Purchase Revenue (Daily)">
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={coinRev}>
                <defs>
                  <linearGradient id="lbRevGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FF9C15" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#FF9C15" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="day" stroke="#666" tick={{ fill: '#999', fontSize: 10 }} />
                <YAxis stroke="#666" />
                <Tooltip contentStyle={tooltipStyle} />
                <Area type="monotone" dataKey="totalRevenue" stroke="#FF9C15" fill="url(#lbRevGrad)" name="Revenue ($)" />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Notification Effectiveness">
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={notifData}>
                <XAxis dataKey="name" stroke="#666" tick={{ fill: '#999', fontSize: 10 }} />
                <YAxis stroke="#666" />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="sent" fill="#FF9C15" name="Sent" />
                <Bar dataKey="clicked" fill="#34d399" name="Clicked" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        {/* Badge Distribution + Notification Prefs */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ChartCard title="Badge Distribution">
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={badgeD}>
                <XAxis dataKey="name" stroke="#666" tick={{ fill: '#999', fontSize: 10 }} />
                <YAxis stroke="#666" />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="totalEarned" fill="#FF9C15" radius={[6, 6, 0, 0]} name="Total Earned" />
                <Bar dataKey="uniqueUsers" fill="#38bdf8" radius={[6, 6, 0, 0]} name="Unique Users" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Notification Preferences">
            <div className="space-y-4 pt-4">
              <div className="flex justify-between"><span className="text-gray-400">Total Users with Prefs</span><span className="text-white font-bold">{formatNumber(prefs.totalUsers || 0)}</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Overtake Alerts Enabled</span><span className="text-emerald-400 font-bold">{formatNumber(prefs.overtakeEnabled || 0)}</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Daily Summary Enabled</span><span className="text-emerald-400 font-bold">{formatNumber(prefs.dailyEnabled || 0)}</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Weekly Results Enabled</span><span className="text-emerald-400 font-bold">{formatNumber(prefs.weeklyEnabled || 0)}</span></div>
            </div>
          </ChartCard>
        </div>

        {/* Actions Breakdown */}
        <ChartCard title="Leaderboard Actions (Last 7 Days)">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {actions.map((a: any, i: number) => (
              <div key={i} className="bg-white/5 rounded-xl p-3">
                <p className="text-xs text-gray-500">{(a.action || '').replace(/_/g, ' ')}</p>
                <p className="text-lg font-bold text-white">{formatNumber(a.count)}</p>
                <p className="text-xs text-gray-500">{formatNumber(a.uniqueUsers)} users</p>
              </div>
            ))}
            {actions.length === 0 && <p className="text-gray-500 text-sm col-span-4">No action data yet</p>}
          </div>
        </ChartCard>

        {/* Daily Trends */}
        <ChartCard title="Daily Trends (30d)">
          {trends.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={(() => {
                const days = Array.from(new Set(trends.map((t: any) => t.day))).sort();
                return days.map(day => {
                  const row: any = { day };
                  trends.filter((t: any) => t.day === day).forEach((t: any) => {
                    row[t.event] = t.count;
                  });
                  return row;
                });
              })()}>
                <XAxis dataKey="day" stroke="#666" tick={{ fill: '#999', fontSize: 10 }} />
                <YAxis stroke="#666" />
                <Tooltip contentStyle={tooltipStyle} />
                <Line type="monotone" dataKey="tasks_view" stroke="#FF9C15" name="Tasks View" dot={false} />
                <Line type="monotone" dataKey="leaderboard_view" stroke="#F42026" name="Leaderboard View" dot={false} />
                <Line type="monotone" dataKey="coin_purchase_flow" stroke="#34d399" name="Coin Purchases" dot={false} />
                <Line type="monotone" dataKey="smart_banner_event" stroke="#38bdf8" name="Banner Events" dot={false} />
                <Line type="monotone" dataKey="lb_notification_event" stroke="#c084fc" name="Notifications" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : <p className="text-gray-500 text-sm">No trend data yet</p>}
        </ChartCard>

        <p className="text-xs text-gray-600 text-right">Updated: {lb.updatedAt ? new Date(lb.updatedAt).toLocaleString() : 'N/A'}</p>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'overview': return renderOverview();
      case 'realtime': return renderRealtime();
      case 'users': return renderUsers();
      case 'videos': return renderVideos();
      case 'engagement': return renderEngagement();
      case 'topUsers': return renderTopUsers();
      case 'geography': return renderGeography();
      case 'aiLens': return renderAiLens();
      case 'devices': return renderDevices();
      case 'scans': return renderScans();
      case 'subscriptions': return renderSubscriptions();
      case 'revenue': return renderRevenue();
      case 'userJourney': return renderUserJourney();
      case 'userSegments': return renderUserSegments();
      case 'leaderboard': return renderLeaderboard();
    }
  };

  if (loading && !overview) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <Navbar />
        <div className="flex items-center justify-center" style={{ minHeight: 'calc(100vh - 80px)' }}>
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-[#FF9C15] border-t-transparent rounded-full animate-spin" />
            <p className="text-gray-400">Loading analytics...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !overview) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <Navbar />
        <div className="flex items-center justify-center" style={{ minHeight: 'calc(100vh - 80px)' }}>
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="w-12 h-12 bg-red-500/10 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <p className="text-gray-400">Failed to load analytics</p>
            <p className="text-gray-500 text-sm">{error}</p>
            <button
              onClick={loadAllData}
              className={`px-6 py-2 bg-gradient-to-r ${brandGradient} text-white rounded-lg font-medium hover:opacity-90 transition-opacity`}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <Navbar />

      <div className="relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-[#FF9C15]/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-[#F42026]/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-8">
          {/* Compact tab row with inline refresh + last-updated */}
          <div className="flex items-center gap-1 mb-4 overflow-x-auto">
            <button
              onClick={loadAllData}
              disabled={loading}
              title={`Last updated ${lastRefresh.toLocaleTimeString()}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-gradient-to-r ${brandGradient} text-white hover:opacity-90 transition-all shadow-sm disabled:opacity-50 flex-shrink-0 mr-1`}
            >
              <svg className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all duration-200 whitespace-nowrap ${
                  activeTab === tab.key
                    ? `bg-gradient-to-r ${brandGradient} text-white shadow`
                    : 'bg-white/5 text-gray-300 hover:bg-white/10 border border-white/10'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default Analytics;
